import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, ChevronDown, ChevronRight, GripVertical, MoreHorizontal,
  AlertTriangle, Trash2, X, Loader2, Check, FileText, Sparkles,
  Maximize2, Minimize2, Filter, BookPlus, History, Keyboard, RotateCcw, Undo2, RefreshCw, Archive
} from "lucide-react";
import Flag from "lucide-react/dist/esm/icons/flag";
import Bookmark from "lucide-react/dist/esm/icons/bookmark";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor,
  closestCenter, useSensor, useSensors, useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { api } from "../lib/api";
import { gbp, num } from "../lib/format";
import { templateErrorMessage, templateReportSummary } from "../lib/jobTemplates";
import {
  computeEstimate, makeBlankLine, makeBlankPhase, makeBlankSubPhase, newId
} from "../lib/estimate";
import {
  applyOverrideDetection, applyRecipeArraysToLine, lineFromLibrary, lineQty, reapplyRecipe,
  applyLineWastageOverride
} from "../lib/recipe";
import { LineWastageBadge } from "../components/LineWastagePopover";
import { AnchoredPortal } from "../components/AnchoredPortal";
import { LibraryPickerModal } from "../components/LibraryPickerModal";
import { TypeBadge, GroupTh, SubTh, GCell } from "../components/EstimateTable";
import { RefreshFromLibraryDialog } from "../components/RefreshFromLibraryDialog";
import { BulkRefreshDialog } from "../components/BulkRefreshDialog";
import { WastageInsightsPanel } from "../components/WastageInsightsPanel";
import { FlatMaterialsPanel } from "../components/FlatMaterialsPanel";
import {
  ID, parseId, reorderPhases, moveSubPhase, moveLine,
  resolveLineDropTarget, resolveSubPhaseDropTarget,
  findLine, findSubPhase,
} from "../lib/dnd";
import {
  Card, EmptyState, Field, Input, NumberInput,
  PrimaryButton, QuietButton
} from "../components/UI";
import { RecipeReadView, RecipeDrawer } from "../components/RecipeDrawer";
import { SaveToLibraryModal } from "../components/SaveToLibraryModal";
import InternalNotePopover, { InternalNoteGlyph } from "../components/InternalNotePopover";
import LabourRateStalenessBanner from "../components/LabourRateStalenessBanner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";

/* ===================== helpers ===================== */
const cls = (...a) => a.filter(Boolean).join(" ");
const hrs = (n) => n == null ? "—" : `${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;


/* ------------------------------------------------------------------
   LT-Estimate-UX-01 Section 3 — pricing override helpers
   Row is considered "edited" if any pricing cell diff is set. Description
   edits are tracked separately (`description_edited`) and never contribute
   to the amber pricing signal. Only applies to library lines with a snapshot;
   ad-hoc lines always report false.
------------------------------------------------------------------ */
function anyPricingOverride(line) {
  if (!line) return false;
  // LT-Estimate-UX-01 Section 3 — only library lines with a snapshot can be
  // "edited". Ad-hoc lines (or library lines that the estimator has toggled
  // to ad-hoc via the badge) never surface the amber pricing signal, per
  // user-locked spec.
  if (!line.recipe_snapshot) return false;
  if (line.type !== "library") return false;
  if (line.hours_override || line.hourly_rate_override) return true;
  if (line.material_rate_override || line.plant_rate_override || line.subby_rate_override) return true;
  if (line.unit_override) return true;
  const snapExtras = line.recipe_snapshot.additional_trades || [];
  const lineExtras = line.additional_trades || [];
  if (lineExtras.length !== snapExtras.length) return true;
  for (const t of lineExtras) {
    if (t.hours_override || t.hourly_rate_override) return true;
  }
  return false;
}

const COMMON_UNITS = ["m", "m²", "m³", "kg", "nr", "litre", "hr", "day", "set", "sum", "PS", "item"];

/* ===================== save status badge ===================== */
function SaveBadge({ status, lastSaved }) {
  let label, bg, fg, dot, Icon;
  if (status === "saving") {
    label = "Saving…"; bg = "#FEF3E2"; fg = "#B45309"; dot = "#D97706"; Icon = Loader2;
  } else if (status === "error") {
    label = "Save failed"; bg = "#F4E6E4"; fg = "#9B3A37"; dot = "#B91C1C"; Icon = AlertTriangle;
  } else if (status === "saved") {
    label = lastSaved ? `Saved at ${lastSaved}` : "All changes saved";
    bg = "#E8EFE9"; fg = "#2F4F3E"; dot = "#3F6E55"; Icon = Check;
  } else {
    label = "Idle"; bg = "#F0EEE6"; fg = "#6B6B6B"; dot = "#A0A0A0"; Icon = null;
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md"
      style={{ background: bg, color: fg, fontSize: 11, fontWeight: 500, border: `1px solid ${dot}22` }}>
      {Icon ? <Icon size={11} className={status === "saving" ? "animate-spin" : ""} /> :
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />}
      {label}
    </span>
  );
}

/* ===================== type badge (READ-ONLY provenance) =====================
   LT-33 correction: the TYPE badge is a PROVENANCE marker, set at creation and
   NON-editable. It records origin only — LIBRARY (pulled from the Master
   Library, has a library_item_id) or AD-HOC (typed manually). It must NOT be
   user-switchable: switching an ad-hoc line to LIBRARY previously only set
   line.type="library" WITHOUT any library_item_id, so downstream readers were
   told the line had a library item behind it when it did not. Display-only. */
/* ===================== keyboard shortcuts panel =====================

Section 2 · Discoverability of the cell-level keyboard model. Opens from
a Keyboard button in the estimate toolbar (icon Keyboard). Also opens on
"?" from anywhere on the page.

*/
function KeyboardShortcutsDialog({ open, onOpenChange }) {
  const Row = ({ keys, desc }) => (
    <div className="flex items-baseline justify-between gap-4 py-1.5" data-testid={`kbd-shortcut-${desc.split(" ")[0].toLowerCase()}`}>
      <span className="flex items-center gap-1">
        {keys.map((k, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-ink-4" style={{ fontSize: 11 }}>then</span>}
            <kbd className="inline-block px-1.5 py-0.5 rounded border border-line bg-surface-2 font-mono text-ink"
              style={{ fontSize: 11, minWidth: 24, textAlign: "center" }}>
              {k}
            </kbd>
          </React.Fragment>
        ))}
      </span>
      <span className="text-ink-2" style={{ fontSize: 12.5 }}>{desc}</span>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="kbd-shortcuts-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts · Estimate</DialogTitle>
          <DialogDescription>
            Fast cell-to-cell movement while entering line items.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <h4 className="text-ink font-medium mb-2" style={{ fontSize: 13 }}>Move across a row</h4>
          <Row keys={["Tab"]}       desc="Next editable cell to the right" />
          <Row keys={["Shift", "Tab"]} desc="Previous editable cell to the left" />
          <div className="mt-1 text-ink-3" style={{ fontSize: 11.5 }}>
            Ref, computed totals, badges and the row menu are skipped.
          </div>
        </div>

        <div className="mt-4">
          <h4 className="text-ink font-medium mb-2" style={{ fontSize: 13 }}>Move down / up a column</h4>
          <Row keys={["Enter"]}          desc="Same column, next row"      />
          <Row keys={["Shift", "Enter"]} desc="Same column, previous row"  />
          <div className="mt-1 text-ink-3" style={{ fontSize: 11.5 }}>
            Only in the numeric rate columns: Hours, £/hr, Material, Plant, Subby.
            First / last row is a silent no-op — rows are never auto-created.
          </div>
        </div>

        <div className="mt-4">
          <h4 className="text-ink font-medium mb-2" style={{ fontSize: 13 }}>Cancel an edit</h4>
          <Row keys={["Esc"]} desc="Revert cell to its previous value" />
          <div className="mt-1 text-ink-3" style={{ fontSize: 11.5 }}>
            Applies to every editable cell. Focus stays in the cell so you can retry.
          </div>
        </div>

        <DialogFooter className="mt-4 text-ink-4" style={{ fontSize: 11 }}>
          Press <kbd className="inline-block px-1.5 py-0.5 rounded border border-line bg-surface-2 font-mono mx-1" style={{ fontSize: 10.5 }}>?</kbd>
          anywhere on the estimate to open this panel.
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ===================== update library from line dialog =====================

LT-Estimate-UX-01 Section 3 · Push the current line's edited values back
onto the master library item. Reuses `POST /api/library/{id}/push-from-tender`
with the extended payload (optional description + unit). Both description
and unit push default OFF — user must tick each explicitly. The recipe
arrays pushed are derived from the current line values (per user spec: we
push what the estimator has been working on, not the stale snapshot).

*/
function UpdateLibraryFromLineDialog({ open, sourceLine, tenderId, onClose, onSuccess }) {
  const [busy, setBusy] = React.useState(false);
  const [pushDescription, setPushDescription] = React.useState(false);
  const [pushUnit, setPushUnit] = React.useState(false);
  const [errText, setErrText] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setPushDescription(false);
      setPushUnit(false);
      setErrText("");
      setBusy(false);
    }
  }, [open]);

  if (!sourceLine || !sourceLine.recipe_snapshot?.library_item_id) return null;

  const snap = sourceLine.recipe_snapshot || {};
  const qty = Number(sourceLine.qty) || 1;
  const hoursPerUnit = qty > 0 && sourceLine.hours != null
    ? Number((Number(sourceLine.hours) / qty).toFixed(4)) : 0;
  const hourlyRate = Number(sourceLine.hourly_rate || 0);
  const primaryTrade = sourceLine.trade_name || snap.trade_name || "";

  // Human-readable diff preview so the user can see exactly what they're
  // about to push. Only shows the fields that differ. Non-pricing edits
  // (description) also appear here when opted-in.
  const rows = [];
  const push = (label, wasLbl, was, nowLbl, now, isOverridden) => {
    if (isOverridden) rows.push({ label, was, now, wasLbl, nowLbl });
  };
  push("Hours (per unit)", "was", (Number(snap.hours_per_unit) || 0).toFixed(2),
       "now", hoursPerUnit.toFixed(2),
       !!sourceLine.hours_override);
  push("Hourly rate", "was", `£${(Number(snap.hourly_rate) || 0).toFixed(2)}`,
       "now", `£${hourlyRate.toFixed(2)}`,
       !!sourceLine.hourly_rate_override);
  push("Material rate", "was", `£${(Number(snap.material_rate) || 0).toFixed(2)}`,
       "now", `£${Number(sourceLine.material_rate || 0).toFixed(2)}`,
       !!sourceLine.material_rate_override);
  push("Plant rate", "was", `£${(Number(snap.plant_rate) || 0).toFixed(2)}`,
       "now", `£${Number(sourceLine.plant_rate || 0).toFixed(2)}`,
       !!sourceLine.plant_rate_override);
  push("Subby rate", "was", `£${(Number(snap.subby_rate) || 0).toFixed(2)}`,
       "now", `£${Number(sourceLine.subby_rate || 0).toFixed(2)}`,
       !!sourceLine.subby_rate_override);

  const doPush = async () => {
    setBusy(true);
    setErrText("");
    try {
      // Recipe payload — from CURRENT line values (per user: push edits, not
      // stale snapshot). Uses the same shape as the SaveToLibraryModal so the
      // backend `push-from-tender` endpoint applies it identically.
      const labour = [];
      if (hoursPerUnit > 0 || hourlyRate > 0 || primaryTrade) {
        labour.push({
          trade_name: primaryTrade,
          hours_per_unit: hoursPerUnit,
          hourly_rate: hourlyRate,
          labour_rate_id: null,
        });
      }
      for (const t of (sourceLine.additional_trades || [])) {
        const th = qty > 0 && t.hours != null ? Number(t.hours) / qty : 0;
        labour.push({
          trade_name: t.trade_name || "",
          hours_per_unit: Number(th.toFixed(4)),
          hourly_rate: Number(t.hourly_rate || 0),
          labour_rate_id: null,
        });
      }
      // LT-36 — a Flat Material Rate line stays flat on the master (figure travels).
      const flatSnap = sourceLine.recipe_snapshot?.flat_material || null;
      const materials = (!flatSnap && Number(sourceLine.material_rate || 0) > 0) ? [{
        description: "Materials per unit",
        qty_per_unit: 1,
        unit: sourceLine.unit || "",
        rate: Number(sourceLine.material_rate),
        material_id: null,
      }] : [];
      const plant = Number(sourceLine.plant_rate || 0) > 0 ? [{
        description: "Plant per unit",
        cost_per_unit: Number(sourceLine.plant_rate),
      }] : [];
      const subby = Number(sourceLine.subby_rate || 0) > 0 ? [{
        description: "Subby per unit",
        cost_per_unit: Number(sourceLine.subby_rate),
      }] : [];

      const payload = {
        recipe_labour: labour,
        recipe_materials: materials,
        recipe_plant: plant,
        recipe_subby: subby,
        flat_material: flatSnap ? { rate: Number(sourceLine.material_rate || 0), note: flatSnap.note || null } : null,
        source_tender_id: tenderId,
        source_line_id: sourceLine.id,
        source_line_description: sourceLine.description || "",
      };
      // Optional pushes — server treats missing fields as "leave as-is".
      if (pushDescription && sourceLine.description) {
        payload.description = sourceLine.description;
      }
      if (pushUnit && sourceLine.unit) {
        payload.unit = sourceLine.unit;
      }

      const libId = sourceLine.recipe_snapshot.library_item_id;
      const r = await api.post(`/library/${libId}/push-from-tender`, payload);
      toast.success("Library item updated");
      onSuccess?.(r.data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Update failed";
      setErrText(String(msg));
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const hasDescriptionEdit = !!sourceLine.description_edited;
  const hasUnitEdit = !!sourceLine.unit_override;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="update-library-dialog">
        <DialogHeader>
          <DialogTitle>Update library item with these values</DialogTitle>
          <DialogDescription>
            Push the pricing edits on this line back onto the master library item
            so future tenders start with the same values. Nothing is written
            until you confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="rounded-md border border-line bg-surface-2 px-3 py-2" style={{ fontSize: 12 }}>
            <div className="text-ink-3 mb-1" style={{ fontSize: 11 }}>Library item</div>
            <div className="text-ink font-medium">
              {snap.library_item_description || "(unnamed)"}
            </div>
          </div>

          <div>
            <div className="col-head mb-1">Changes to push</div>
            {rows.length === 0 ? (
              <div className="text-ink-3 italic" style={{ fontSize: 12 }}>
                No pricing changes detected. Add an override on the line first.
              </div>
            ) : (
              <ul className="space-y-1">
                {rows.map((r, idx) => (
                  <li key={idx} className="flex items-baseline justify-between gap-3"
                    data-testid={`update-lib-diff-${r.label.replace(/\s+/g, '-').toLowerCase()}`}
                    style={{ fontSize: 12.5 }}>
                    <span className="text-ink-2">{r.label}</span>
                    <span className="font-mono tnum text-ink-3" style={{ fontSize: 12 }}>
                      <span className="text-ink-3">{r.was}</span>
                      <span className="mx-1 text-ink-4">→</span>
                      <span className="text-warning font-semibold">{r.now}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-line pt-3 space-y-2">
            <div className="col-head">Also push (optional)</div>
            <label className={cls(
              "flex items-start gap-2 rounded px-2 py-1.5",
              !hasDescriptionEdit && "opacity-50"
            )}>
              <Checkbox
                checked={pushDescription}
                disabled={!hasDescriptionEdit}
                onCheckedChange={(v) => setPushDescription(!!v)}
                data-testid="update-lib-push-description"
                className="mt-0.5"
              />
              <span className="flex-1" style={{ fontSize: 12.5 }}>
                <span className="text-ink block">Description</span>
                <span className="text-ink-3" style={{ fontSize: 11.5 }}>
                  {hasDescriptionEdit
                    ? `Was: "${snap.description || ""}". Push new: "${sourceLine.description || ""}"`
                    : "No description edits on this line"}
                </span>
              </span>
            </label>
            <label className={cls(
              "flex items-start gap-2 rounded px-2 py-1.5",
              !hasUnitEdit && "opacity-50"
            )}>
              <Checkbox
                checked={pushUnit}
                disabled={!hasUnitEdit}
                onCheckedChange={(v) => setPushUnit(!!v)}
                data-testid="update-lib-push-unit"
                className="mt-0.5"
              />
              <span className="flex-1" style={{ fontSize: 12.5 }}>
                <span className="text-ink block">Unit</span>
                <span className="text-ink-3" style={{ fontSize: 11.5 }}>
                  {hasUnitEdit
                    ? `Was: ${snap.unit || "—"}. Push new: ${sourceLine.unit || "—"}`
                    : "No unit change on this line"}
                </span>
              </span>
            </label>
          </div>

          {errText && (
            <div className="rounded-md border border-note-red/40 bg-note-red/10 px-3 py-2 text-note-red"
              style={{ fontSize: 12 }} data-testid="update-lib-error">
              {errText}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}
            data-testid="update-lib-cancel">
            Cancel
          </Button>
          <Button type="button" onClick={doPush} disabled={busy || rows.length === 0}
            data-testid="update-lib-confirm">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Pushing…</> : "Update library item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* ===================== editable cells =====================

Section 2 (LT-Kbd-Nav) · shared keyboard behaviour for editable cells:
  - Escape reverts to the value at focus-in and keeps focus in the cell.
    A mistyped-then-Escape'd cell never fires onChange with a different
    value than what was there originally, so the override flag never
    latches on a mistake.
  - Enter / Shift+Enter on the FIVE numeric rate columns (marked with
    `navCol` on NumCell) moves focus to the same column in the next /
    previous row of the same phase table. Last-row / first-row = silent
    no-op — we never auto-add rows on Enter.
  - Enter on text cells is neutralised so it can never trigger an
    accidental form submit.

Section 3 (LT-Estimate-UX-01) · manual toggle removed:
  - Override state is purely value-derived (diff vs `recipe_snapshot`) by
    `applyOverrideDetection`. The dot is display-only; there is no click
    to "mark as manual override".
  - When a cell is overridden AND we know the snapshot baseline, hovering
    the dot reveals a small ↶ revert button that resets the cell to its
    snapshot value.
  - Tooltip is set to `Was <baseline> at insert → now <current>`.

Design note on `data-nav-col`: it lives on the numeric <input> itself
rather than the wrapper so `input.closest('table')` scoping works when
we find the next-row sibling. Table-scoped so Enter never jumps out of
one phase into another.

*/

function _focusColInSiblingRow(fromInput, direction /* +1 or -1 */) {
  const col = fromInput.getAttribute("data-nav-col");
  const table = fromInput.closest("table");
  if (!col || !table) return;
  const all = Array.from(table.querySelectorAll(`input[data-nav-col="${col}"]`));
  const idx = all.indexOf(fromInput);
  if (idx === -1) return;
  const target = all[idx + direction];
  if (!target) return;                     // last / first row — silent no-op
  target.focus();
  // Site-wide select-on-focus semantics match onFocus in NumCell.
  requestAnimationFrame(() => { try { target.select(); } catch (_) {} });
}

/**
 * Format a numeric baseline value for use in override tooltips. Prefixes with
 * £ when `symbol==="money"`; suffixes with unit when `symbol==="hours"`;
 * otherwise plain 2dp. Handles null/undefined by returning a dash.
 */
function fmtBaseline(v, symbol) {
  if (v == null || Number.isNaN(Number(v))) return "\u2014";
  const n = Number(v);
  const s = n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (symbol === "money") return `\u00a3${s}`;
  if (symbol === "hours") return `${n.toLocaleString("en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}h`;
  return s;
}

function NumCell({
  value, onChange, override,
  align = "right", placeholder = "\u2014", suffix, navCol,
  // LT-Estimate-UX-01 Section 3 — snapshot baseline for tooltips + revert.
  baselineValue, baselineSymbol, onRevert, revertLabel, testId
}) {
  const [focused, setFocused] = useState(false);
  const initialRef = React.useRef(null);   // value at focus-in, for Escape-revert
  const tooltip = override
    ? `Was ${fmtBaseline(baselineValue, baselineSymbol)} at insert \u2192 now ${fmtBaseline(value, baselineSymbol)}`
    : undefined;
  return (
    <div className={cls("group relative flex items-center gap-1", align === "right" ? "justify-end" : "")}>
      {override ? (
        <span
          className="relative inline-flex flex-shrink-0 items-center"
          data-testid={testId ? `${testId}-override` : undefined}
        >
          <span
            className="block rounded-full cursor-help"
            style={{ width: 6, height: 6, background: "#D97706" }}
            title={tooltip}
            aria-label={tooltip}
          />
          {onRevert && (
            <button
              type="button"
              tabIndex={-1}
              onClick={onRevert}
              title={revertLabel || `Revert to ${fmtBaseline(baselineValue, baselineSymbol)}`}
              aria-label={revertLabel || `Revert to ${fmtBaseline(baselineValue, baselineSymbol)}`}
              className="ml-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity text-warning hover:text-warning-strong"
              data-testid={testId ? `${testId}-revert` : undefined}
            >
              <Undo2 size={10} />
            </button>
          )}
        </span>
      ) : null}
      <input
        type="number"
        step="0.01"
        data-nav-col={navCol || undefined}
        data-testid={testId || undefined}
        value={value === null || value === undefined ? "" : value}
        placeholder={focused ? "" : placeholder}
        onFocus={(e) => {
          setFocused(true);
          initialRef.current = value;
          // Site-wide: select existing value on focus so a fresh keystroke replaces it.
          requestAnimationFrame(() => { try { e.target.select(); } catch (_) {} });
        }}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onChange(initialRef.current);
            return;
          }
          // Enter / Shift+Enter — only on the five nav columns.
          if (navCol && e.key === "Enter") {
            e.preventDefault();          // never let it submit the outer form
            _focusColInSiblingRow(e.currentTarget, e.shiftKey ? -1 : +1);
          }
        }}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        title={tooltip}
        className={cls(
          "w-full bg-transparent border-0 outline-none rounded px-1 py-1 font-mono tnum",
          "focus:bg-bg focus:ring-1 focus:ring-accent/40",
          override ? "text-warning font-semibold" : "text-ink",
          align === "right" ? "text-right" : "text-left"
        )}
        style={{ fontSize: 12 }}
      />
      {suffix && <span className="text-ink-4" style={{ fontSize: 10.5 }}>{suffix}</span>}
    </div>
  );
}

function TextCell({ value, onChange, placeholder, align = "left", mono = false }) {
  const initialRef = React.useRef(null);
  return (
    <input
      type="text"
      value={value || ""}
      placeholder={placeholder}
      spellCheck="true"
      onChange={(e) => onChange(e.target.value)}
      onFocus={(e) => {
        initialRef.current = value;
        // Site-wide: select existing value on focus so a fresh keystroke replaces it.
        requestAnimationFrame(() => { try { e.target.select(); } catch (_) {} });
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onChange(initialRef.current);
          return;
        }
        if (e.key === "Enter") {
          // Text cells keep conventional behaviour — do nothing but stop
          // the event from bubbling to any ancestor form / submit handler.
          e.preventDefault();
        }
      }}
      className={cls(
        "w-full bg-transparent border-0 outline-none rounded px-1.5 py-1",
        "focus:bg-bg focus:ring-1 focus:ring-accent/40",
        "text-ink",
        align === "right" ? "text-right" : "text-left",
        mono && "font-mono tnum"
      )}
      style={{ fontSize: align === "left" && !mono ? 12.5 : 12 }}
    />
  );
}

/* LT-17 §7c — TradeCell.
 *
 * Trade picker for the Estimate line editor. Replaces the previous
 * free-text `<TextCell>` on the primary trade column and the inline
 * `<input type="text">` on the secondary-trades row. On selection,
 * populates:
 *   • line.trade_name (or additional_trades[i].trade_name)
 *   • line.labour_rate_id (or additional_trades[i].labour_rate_id)
 *   • line.hourly_rate (or additional_trades[i].hourly_rate) — from
 *     the master rate, resetting `hourly_rate_override` to false so
 *     the row reads as a fresh library link.
 *
 * Free-typing is still allowed (a user CAN enter a trade that isn't in
 * the master list). Free-typed trades are saved with
 * `labour_rate_id=null` and remain invisible to the LT-17 §7d/§7e
 * staleness + reprice paths — that's tracked deliberately, not
 * silently.
 *
 * Props:
 *   value        — current trade_name string.
 *   labourRates  — master labour_rates list, used for the dropdown.
 *   onChange     — called with a PATCH object. On selection, the patch
 *                  carries `{ trade_name, labour_rate_id, hourly_rate,
 *                  hourly_rate_override: false }`. On free-text edit
 *                  (typing that doesn't match a rate), the patch is
 *                  `{ trade_name, labour_rate_id: null }`.
 *   compact      — `true` for the inline secondary-trades chip layout
 *                  (narrower input, smaller font).
 *   testId       — data-testid on the input.
 */
function TradeCell({ value, labourRates, onChange, placeholder = "Trade", compact = false, testId }) {
  const [open, setOpen] = useState(false);
  const inputRef = React.useRef(null);
  const initialRef = React.useRef(null);
  const options = React.useMemo(
    () => (labourRates || []).slice().sort(
      (a, b) => (a.trade_name || "").localeCompare(b.trade_name || "")
    ),
    [labourRates],
  );
  const filtered = React.useMemo(() => {
    const q = (value || "").toLowerCase().trim();
    const list = q
      ? options.filter((o) =>
          (o.trade_name || "").toLowerCase().includes(q)
          || (o.trade_code || "").toLowerCase().includes(q))
      : options;
    return list.slice(0, 12);
  }, [options, value]);
  const applySelection = (lr) => {
    onChange({
      trade_name:            lr.trade_name,
      labour_rate_id:        lr.id,
      hourly_rate:           Number(lr.hourly_rate ?? 0),
      hourly_rate_override:  false,
    });
    setOpen(false);
  };
  const applyFreeText = (typed) => {
    // Trade rename / free-text — never carries a labour_rate_id.
    // Leaves hourly_rate alone so a partial edit doesn't wipe a
    // value the user has already entered.
    onChange({ trade_name: typed, labour_rate_id: null });
  };
  return (
    <div className={cls("relative", compact ? "" : "flex-1 min-w-0")}>
      <input
        ref={inputRef}
        type="text"
        value={value || ""}
        placeholder={placeholder}
        spellCheck="true"
        data-testid={testId}
        onChange={(e) => { applyFreeText(e.target.value); setOpen(true); }}
        onFocus={(e) => {
          initialRef.current = value;
          setOpen(true);
          requestAnimationFrame(() => { try { e.target.select(); } catch (_) {} });
        }}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            applyFreeText(initialRef.current || "");
            setOpen(false);
          }
          if (e.key === "Enter") {
            e.preventDefault();
            setOpen(false);
          }
        }}
        className={cls(
          "bg-transparent border-0 outline-none rounded px-1.5 py-1",
          "focus:bg-bg focus:ring-1 focus:ring-accent/40 text-ink text-left",
          compact ? "w-24" : "w-full",
        )}
        style={{ fontSize: compact ? 11.5 : 12.5 }}
      />
      {/* Body portal — the phase table's overflow container must never clip
          the list; AnchoredPortal flips it above the cell when needed. */}
      <AnchoredPortal anchorRef={inputRef} open={open && filtered.length > 0}
        className="bg-surface border border-line rounded-md shadow-card max-h-56 overflow-auto"
        style={{ minWidth: 240, width: "max-content", maxWidth: 420 }}
        data-testid={testId ? `${testId}-options` : undefined}
      >
          {filtered.map((lr) => (
            <button
              key={lr.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); applySelection(lr); }}
              className="w-full text-left px-3 py-1.5 hover:bg-surface-2 flex items-center justify-between gap-3"
              style={{ fontSize: 11.5 }}
              data-testid={`trade-picker-option-${lr.trade_code || lr.id}`}
            >
              <span className="text-ink truncate">
                {lr.trade_name}
                {lr.trade_code
                  && <span className="ml-1.5 font-mono text-ink-3">({lr.trade_code})</span>}
              </span>
              <span className="font-mono tnum text-ink-3">
                £{Number(lr.hourly_rate || 0).toFixed(2)}/hr
              </span>
            </button>
          ))}
      </AnchoredPortal>
    </div>
  );
}

/* LT-33 — Line Item Note. Sits directly below the description in the same
   cell. Client-facing, free text (unbounded), pre-filled from the library
   item's default_note at insert then freely editable/clearable per line with
   NO write-back to the library. Screen styling: black, italic, ~1.5pt smaller
   than the description (PDF styling — red — is a later build). An EMPTY note is
   invisible (a low-weight "+ note" affordance, not an empty box). */
function NoteCell({ value, onChange, lineId, readOnly = false }) {
  const has = !!(value && value.trim());
  const [editing, setEditing] = React.useState(false);
  const taRef = React.useRef(null);

  const autosize = (el) => {
    if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; }
  };

  React.useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      const len = taRef.current.value.length;
      try { taRef.current.setSelectionRange(len, len); } catch (_) { /* ignore */ }
    }
  }, [editing]);

  // Read-only (e.g. Adjudication view): render text only, nothing when empty.
  if (readOnly) {
    if (!has) return null;
    return (
      <div
        data-testid={`note-readonly-${lineId}`}
        className="italic text-ink px-1.5 mt-0.5"
        style={{ fontSize: 11, lineHeight: 1.4, whiteSpace: "pre-wrap" }}
      >
        {value}
      </div>
    );
  }

  if (!has && !editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        onFocus={() => setEditing(true)}
        data-testid={`add-note-${lineId}`}
        className="mt-0.5 inline-flex items-center gap-1 text-ink-4 hover:text-accent transition-colors"
        style={{ fontSize: 10.5, opacity: 0.55 }}
      >
        <Plus size={9} /> note
      </button>
    );
  }

  return (
    <textarea
      ref={(el) => { taRef.current = el; autosize(el); }}
      value={value || ""}
      onChange={(e) => { autosize(e.target); onChange(e.target.value); }}
      onBlur={() => setEditing(false)}
      placeholder="Add a note…"
      rows={1}
      spellCheck="true"
      data-testid={`note-${lineId}`}
      className={cls(
        "w-full bg-transparent border-0 outline-none rounded px-1.5 py-0.5 mt-0.5",
        "italic text-ink resize-none overflow-hidden",
        "focus:bg-bg focus:ring-1 focus:ring-accent/40",
      )}
      style={{ fontSize: 11, lineHeight: 1.4 }}
    />
  );
}

function UnitCell({ value, onChange, override, baselineValue, onRevert }) {
  const initialRef = React.useRef(null);
  const tooltip = override
    ? `Was ${baselineValue || "\u2014"} at insert \u2192 now ${value || "\u2014"}`
    : undefined;
  return (
    <div className="group relative flex items-center gap-1 justify-end">
      {override ? (
        <span className="relative inline-flex flex-shrink-0 items-center">
          <span
            className="block rounded-full cursor-help"
            style={{ width: 6, height: 6, background: "#D97706" }}
            title={tooltip}
            aria-label={tooltip}
          />
          {onRevert && (
            <button
              type="button"
              tabIndex={-1}
              onClick={onRevert}
              title={`Revert to ${baselineValue || "\u2014"}`}
              aria-label={`Revert unit to ${baselineValue || "\u2014"}`}
              className="ml-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity text-warning hover:text-warning-strong"
            >
              <Undo2 size={10} />
            </button>
          )}
        </span>
      ) : null}
      <select
        value={value || ""}
        onFocus={() => { initialRef.current = value; }}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onChange(initialRef.current);
          }
        }}
        title={tooltip}
        className={cls(
          "w-full bg-transparent border-0 outline-none rounded px-1 py-1 font-mono text-right",
          "focus:bg-bg focus:ring-1 focus:ring-accent/40",
          override ? "text-warning font-semibold" : "text-ink-2"
        )}
        style={{ fontSize: 11.5 }}
      >
        <option value=""></option>
        {COMMON_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
      </select>
    </div>
  );
}

/* ===================== row "..." menu ===================== */
// Phase 1.5 (B/C) — jump-to-line focus. Provided by the top TenderEstimate
// component so any LineRow can read the current focus target + dismiss it,
// without prop-drilling through the phase/table tree.
const FocusLineContext = React.createContext({ focusLineId: null, dismissFocus: () => {} });

/* LT-21a §5 — line_id -> library-drift row for lines whose library item is
 * archived. Read-only marker; the line keeps its snapshot. */
const ArchivedRefsContext = React.createContext({});

function ArchivedLibraryMarker({ lineId }) {
  const row = React.useContext(ArchivedRefsContext)?.[lineId];
  if (!row) return null;
  const tip = `Library item "${row.library_item_description || row.library_item_sku || "(unnamed)"}" has been archived. ` +
    `This line keeps the rate it was priced at (\u00a3${Number(row.line_rate ?? 0).toFixed(2)}); nothing is re-priced.`;
  return (
    <span
      title={tip}
      aria-label="Library item archived"
      data-testid={`library-archived-${lineId}`}
      className="flex-shrink-0 inline-flex items-center justify-center mt-1.5 cursor-help"
      style={{ color: "#8A6D3B" }}
    >
      <Archive size={12} />
    </span>
  );
}

/* Item 3 — Internal notes context. Provides `tenderId`, per-line and
 * per-phase counts, and an opener that pops the shared popover for a
 * given scope+target. Populated from the tender GET's additive
 * `internal_note_counts` field on load, refreshed on each mutation. */
const InternalNoteContext = React.createContext({
  tenderId: null,
  counts: { tender_level: 0, by_phase: {}, by_line: {} },
  open: () => {},
});

/* Line-scoped glyph — rendered in the ref-cell after LineFlagPopover. */
function InternalNoteLineGlyph({ lineId, description }) {
  const ctx = React.useContext(InternalNoteContext);
  const count = ctx.counts?.by_line?.[lineId] || 0;
  return (
    <InternalNoteGlyph
      count={count}
      onClick={() => ctx.open("line", lineId, description || "Line")}
      testId={`internal-note-glyph-line-${lineId}`}
    />
  );
}

/* Phase-scoped glyph — rendered on the phase header next to the title. */
function InternalNotePhaseGlyph({ phaseId, phaseName }) {
  const ctx = React.useContext(InternalNoteContext);
  const count = ctx.counts?.by_phase?.[phaseId] || 0;
  return (
    <InternalNoteGlyph
      count={count}
      onClick={() => ctx.open("phase", phaseId, phaseName || "Phase")}
      testId={`internal-note-glyph-phase-${phaseId}`}
    />
  );
}


function RowMenu({
  line, onUpdate, onRemove, subPhases, onMoveToSubPhase,
  onReapplyRecipe, onAddTrade, onSaveToLibrary, onUpdateLibraryFromLine, onOpenFlag,
  onRefreshFromLibrary
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const hasSnapshot = !!line.recipe_snapshot;
  // LT-20 — "Refresh from library" re-snapshots the quantity inputs
  // (wastage / coverage / divisibility / stock length) after a confirm.
  const canRefresh = hasSnapshot && (line.recipe_snapshot?.recipe_materials || []).some((e) => e.material_id);
  // LT-Estimate-UX-01 Section 3 — "Update library item…" is only available
  // on lines derived from a library item AND that currently have any pricing
  // cell diff (otherwise there's nothing new to push).
  const rowEdited = anyPricingOverride(line);
  // LT-34 — flag the underlying LIBRARY item for review from the estimate.
  // Only on library-derived lines (never AD-HOC — no library item behind them).
  const reviewLibId = line.library_item_id || line.recipe_snapshot?.library_item_id;
  const canFlagReview = line.type === "library" && !!reviewLibId;
  const flagForReview = async () => {
    const reason = window.prompt(
      "Flag this library item for review — optional reason (why does the rate look wrong?):",
      "",
    );
    if (reason === null) return; // cancelled
    try {
      const r = await api.post(`/library/${reviewLibId}/review-flag`,
        { reason: reason.trim() || null });
      if (r.data?.already_flagged) {
        toast.warning(
          `Already flagged for review${r.data.review_reason ? `: "${r.data.review_reason}"` : ""}`,
        );
      } else {
        toast.success("Library item flagged for review");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not flag item");
    }
  };
  const canUpdateLibrary = (
    hasSnapshot &&
    !!line.recipe_snapshot?.library_item_id &&
    line.type === "library" &&
    rowEdited
  );

  // Keyboard model: the menu takes focus on open; ↑/↓ (Home/End) move
  // between items, Enter/Space activate (native button click), Escape closes
  // and hands focus back to the ⋯ trigger. Click-away closes via the
  // portal's backdrop, so items can use onClick (keyboard-friendly).
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() =>
      menuRef.current?.querySelector("button")?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(id);
  }, [open]);
  const close = () => setOpen(false);
  const onMenuKeyDown = (e) => {
    const items = Array.from(menuRef.current?.querySelectorAll("button") || []);
    const i = items.indexOf(document.activeElement);
    const go = (n) => { e.preventDefault(); items[(n + items.length) % items.length]?.focus(); };
    if (e.key === "Escape") { e.preventDefault(); close(); btnRef.current?.focus(); }
    else if (e.key === "ArrowDown") go(i + 1);
    else if (e.key === "ArrowUp") go(i - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(items.length - 1);
  };

  return (
    <div className="relative">
      <button type="button" tabIndex={-1} ref={btnRef}
        onClick={() => setOpen((x) => !x)}
        onKeyDown={(e) => { if (e.key === "Escape" && open) { e.preventDefault(); close(); } }}
        data-testid={`row-menu-${line.id}`}
        className="text-ink-4 hover:text-ink-2 opacity-60 group-hover:opacity-100 transition-opacity">
        <MoreHorizontal size={14} />
      </button>
      {/* Body portal: the phase table's overflow container must never clip
          the menu, and it must sit above the next phase / line. */}
      <AnchoredPortal anchorRef={btnRef} open={open} align="right" onDismiss={close}
        className="bg-surface border border-line rounded-md shadow-card overflow-hidden"
        style={{ width: 240 }} data-testid={`row-menu-popover-${line.id}`}>
        <div ref={menuRef} role="menu" onKeyDown={onMenuKeyDown}
          className="[&_button]:outline-none [&_button:focus-visible]:bg-surface-2 [&_button:focus-visible]:ring-1 [&_button:focus-visible]:ring-inset [&_button:focus-visible]:ring-accent/40">
          {hasSnapshot && (
            <button
              onClick={() => { onReapplyRecipe?.(); close(); }}
              data-testid={`row-reset-to-snapshot-${line.id}`}
              className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-accent border-b border-line"
              style={{ fontSize: 12 }}
            >
              <RotateCcw size={12} /> Reset row to library snapshot
            </button>
          )}
          {canRefresh && (
            <button
              onClick={() => { onRefreshFromLibrary?.(); close(); }}
              data-testid={`row-refresh-from-library-${line.id}`}
              className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-ink-2 border-b border-line"
              style={{ fontSize: 12 }}
            >
              <RefreshCw size={12} className="text-accent" /> Refresh quantities from library…
            </button>
          )}
          {/* LT-Estimate-UX-01 Section 3 — push these edited values back to
              the master library item. Reuses `POST /api/library/{id}/push-from-tender`
              with optional description + unit push toggles. */}
          {canUpdateLibrary && (
            <button
              onClick={() => { onUpdateLibraryFromLine?.(); close(); }}
              data-testid={`row-update-library-${line.id}`}
              className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-ink-2 border-b border-line"
              style={{ fontSize: 12 }}
            >
              <BookPlus size={12} className="text-warning" /> Update library item with these values…
            </button>
          )}
          {/* Section 4 — promote this line into a NEW master library item.
              Available on every line per Q7; the modal pre-fills from the
              line's snapshot/cells and the user can edit before saving. */}
          <button
            onClick={() => { onSaveToLibrary?.(); close(); }}
            data-testid={`row-save-to-library-${line.id}`}
            className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-ink-2 border-b border-line"
            style={{ fontSize: 12 }}
          >
            <BookPlus size={12} className="text-accent" /> Save as new library item
          </button>
          <button onClick={() => { (onAddTrade ? onAddTrade() : onUpdate({
            ...line,
            additional_trades: [...(line.additional_trades || []), { id: newId(), trade_name: "", hours: 0, hourly_rate: 0 }]
          })); close(); }}
            className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-ink-2"
            style={{ fontSize: 12 }}>
            <Plus size={12} /> Add trade on this line
          </button>
          {subPhases && subPhases.length > 0 && (
            <div className="border-t border-line">
              <div className="col-head px-3 pt-2 pb-0.5">Move to sub-phase</div>
              <button onClick={() => { onMoveToSubPhase(null); close(); }}
                className="w-full text-left px-3 py-1 hover:bg-surface-2 text-ink-3" style={{ fontSize: 11.5 }}>
                ↑ Phase root
              </button>
              {subPhases.map((sp) => (
                <button key={sp.id} onClick={() => { onMoveToSubPhase(sp.id); close(); }}
                  className="w-full text-left px-3 py-1 hover:bg-surface-2 text-ink-3" style={{ fontSize: 11.5 }}>
                  → {sp.name}
                </button>
              ))}
            </div>
          )}
          {canFlagReview && (
            <button
              onClick={() => { flagForReview(); close(); }}
              data-testid={`row-flag-review-${line.id}`}
              className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-violet-700 border-t border-line"
              style={{ fontSize: 12 }}>
              <Bookmark size={12} /> Flag library item for review
            </button>
          )}
          {/* Phase 0 — line-level flags (blind / review). Opens the same flag
              popover as the ref-cell icon (both routes → one popover). */}
          <button
            onClick={() => { onOpenFlag?.("blind"); close(); }}
            data-testid={`row-flag-blind-${line.id}`}
            className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 border-t border-line"
            style={{ fontSize: 12, color: "var(--status-blind)" }}>
            <Flag size={12} /> {line.flag_type === "blind" ? "Edit assumed flag…" : "Flag as assumed…"}
          </button>
          <button
            onClick={() => { onOpenFlag?.("review"); close(); }}
            data-testid={`row-flag-attention-${line.id}`}
            className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2"
            style={{ fontSize: 12, color: "var(--status-review)" }}>
            <Flag size={12} /> {line.flag_type === "review" ? "Edit review flag…" : "Flag line for review…"}
          </button>
          {line.flag_type && (
            <button
              onClick={() => { onOpenFlag?.(); close(); }}
              data-testid={`row-unflag-${line.id}`}
              className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-ink-2"
              style={{ fontSize: 12 }}>
              <X size={12} /> Manage / clear flag
            </button>
          )}
          <button onClick={() => { if (window.confirm("Delete this line?")) onRemove(); close(); }}
            className="w-full text-left px-3 py-1.5 hover:bg-surface-2 inline-flex items-center gap-2 text-note-red border-t border-line"
            style={{ fontSize: 12 }}>
            <Trash2 size={12} /> Delete line
          </button>
        </div>
      </AnchoredPortal>
    </div>
  );
}

/* ===================== secondary trades row ===================== */
function SecondaryTradesRow({ trades, onUpdate, snapshotTrades = [], qty = 1, labourRates = [] }) {
  const totalH = trades.reduce((s, t) => s + (Number(t.hours) || 0), 0);
  const totalC = trades.reduce((s, t) => s + (Number(t.hours) || 0) * (Number(t.hourly_rate) || 0), 0);

  const update = (id, patch) =>
    onUpdate(trades.map((t) => t.id === id ? { ...t, ...patch } : t));
  const remove = (id) =>
    onUpdate(trades.filter((t) => t.id !== id));

  // LT-Estimate-UX-01 Section 3 — index-aligned baselines. If snapshotTrades
  // is shorter than `trades`, the extra entries have no baseline and are, by
  // definition, an override (extras added by the estimator). Formatting uses
  // the same helpers as NumCell so the UX is consistent.
  const baselineForTrade = (i) => (i < snapshotTrades.length ? snapshotTrades[i] : null);

  return (
    <tr className="border-b border-line-soft" style={{ background: "#FBFAF7" }}>
      <td colSpan={6} className="px-2 py-2 pl-12">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="col-head" style={{ fontSize: 9 }}>+ Other trades on this line</span>
          {trades.map((t, i) => {
            const base = baselineForTrade(i);
            const expectedHours = base ? Number(base.hours_per_unit || 0) * lineQty(qty) : null;
            const expectedRate = base ? Number(base.hourly_rate || 0) : null;
            const hoursOverride = !!t.hours_override || (!base && (Number(t.hours) || 0) > 0);
            const rateOverride = !!t.hourly_rate_override || (!base && (Number(t.hourly_rate) || 0) > 0);
            const hoursTooltip = hoursOverride
              ? (base
                ? `Was ${fmtBaseline(expectedHours, "hours")} at insert \u2192 now ${fmtBaseline(t.hours, "hours")}`
                : "Trade added after insert \u2014 no snapshot baseline")
              : undefined;
            const rateTooltip = rateOverride
              ? (base
                ? `Was ${fmtBaseline(expectedRate, "money")} at insert \u2192 now ${fmtBaseline(t.hourly_rate, "money")}`
                : "Trade added after insert \u2014 no snapshot baseline")
              : undefined;
            return (
              <span key={t.id} className="inline-flex items-center gap-1 bg-surface border border-line rounded px-2 py-1"
                style={{ fontSize: 11 }}>
                <TradeCell
                  value={t.trade_name}
                  labourRates={labourRates}
                  onChange={(patch) => update(t.id, patch)}
                  placeholder="Trade"
                  compact
                  testId={`secondary-trade-picker-${t.id}`}
                />
                {hoursOverride && (
                  <span className="block rounded-full cursor-help" title={hoursTooltip}
                    aria-label={hoursTooltip}
                    style={{ width: 5, height: 5, background: "#D97706" }} />
                )}
                <input type="number" step="0.5" value={t.hours == null ? "" : t.hours}
                  onChange={(e) => update(t.id, { hours: e.target.value === "" ? 0 : Number(e.target.value) })}
                  title={hoursTooltip}
                  className={cls(
                    "w-12 bg-transparent border-0 outline-none text-right font-mono tnum",
                    hoursOverride ? "text-warning font-semibold" : "text-ink-2"
                  )}
                  style={{ fontSize: 11 }} />
                <span className="text-ink-4">h</span>
                <span className="text-ink-4">×</span>
                <span className="text-ink-4">£</span>
                {rateOverride && (
                  <span className="block rounded-full cursor-help" title={rateTooltip}
                    aria-label={rateTooltip}
                    style={{ width: 5, height: 5, background: "#D97706" }} />
                )}
                <input type="number" step="0.01" value={t.hourly_rate == null ? "" : t.hourly_rate}
                  onChange={(e) => update(t.id, { hourly_rate: e.target.value === "" ? 0 : Number(e.target.value) })}
                  title={rateTooltip}
                  className={cls(
                    "w-14 bg-transparent border-0 outline-none text-right font-mono tnum",
                    rateOverride ? "text-warning font-semibold" : "text-ink-2"
                  )}
                  style={{ fontSize: 11 }} />
                <span className="text-ink-4">=</span>
                <span className="font-mono tnum text-ink" style={{ fontSize: 11.5, fontWeight: 500 }}>
                  £{num((Number(t.hours) || 0) * (Number(t.hourly_rate) || 0))}
                </span>
                <button onClick={() => remove(t.id)} className="text-ink-4 hover:text-note-red ml-1">
                  <X size={11} />
                </button>
              </span>
            );
          })}
          <button onClick={() => onUpdate([...trades, { id: newId(), trade_name: "", hours: 0, hourly_rate: 0, labour_rate_id: null }])}
            className="inline-flex items-center gap-1 text-accent hover:text-accent-deep" style={{ fontSize: 11 }}>
            <Plus size={11} /> Add trade
          </button>
        </div>
      </td>
      <td colSpan={13} className="px-2 py-2 text-right">
        <span className="text-ink-3 font-mono tnum" style={{ fontSize: 11 }}>
          Total labour <span className="text-ink-2">{hrs(totalH)}h · {gbp(totalC)}</span>
        </span>
      </td>
    </tr>
  );
}

/* ===================== col group ===================== */
function ColGroup() {
  return (
    <colgroup>
      <col style={{ width: 28 }} />        {/* recipe expand chevron */}
      <col style={{ width: 28 }} />        {/* drag */}
      <col style={{ width: 50 }} />        {/* ref */}
      <col />                              {/* description (flex) */}
      <col style={{ width: 108 }} />       {/* type */}
      <col style={{ width: 130 }} />       {/* trade */}
      <col style={{ width: 60 }} />        {/* qty */}
      <col style={{ width: 60 }} />        {/* unit */}
      <col style={{ width: 70 }} />        {/* hours */}
      <col style={{ width: 70 }} />        {/* £/hr */}
      <col style={{ width: 80 }} />        {/* mat rate */}
      <col style={{ width: 86 }} />        {/* mat total */}
      <col style={{ width: 76 }} />        {/* plant rate */}
      <col style={{ width: 86 }} />        {/* plant total */}
      <col style={{ width: 86 }} />        {/* subby rate */}
      <col style={{ width: 90 }} />        {/* subby total */}
      <col style={{ width: 86 }} />        {/* total rate */}
      <col style={{ width: 100 }} />       {/* line £ */}
      <col style={{ width: 36 }} />        {/* row actions */}
    </colgroup>
  );
}

/* ===================== line row ===================== */
// Phase 1.5 (item 7) — lightweight flag popover (body portal via AnchoredPortal).
// Opened from the ref-cell icon or the overflow menu; sets/edits/clears the
// blind|review flag. Colour is never the sole signal — the icon is always
// present. Draws from the shared status tokens.
function LineFlagPopover({ line, open, setOpen, preset, apply, clear }) {
  const flagged = !!line.flag_type;
  const [type, setType] = useState(line.flag_type || preset || "blind");
  const [reason, setReason] = useState(line.flag_reason || "");
  useEffect(() => {
    if (open) {
      setType(preset || line.flag_type || "blind");
      setReason(line.flag_reason || "");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const color = (t) => (t === "blind" ? "var(--status-blind)" : "var(--status-review)");
  const iconColor = flagged ? color(line.flag_type) : undefined;
  const save = () => { apply(type, reason); setOpen(false); };
  const doClear = () => { clear(); setOpen(false); };
  const btnRef = useRef(null);
  const reasonRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => reasonRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(id);
  }, [open]);
  return (
    <span className="relative inline-flex align-middle ml-1">
      <button
        type="button"
        ref={btnRef}
        data-testid={`line-flag-${line.id}`}
        onClick={() => setOpen((o) => !o)}
        title={flagged
          ? `${line.flag_type === "blind" ? "Assumed — priced on assumption without full information" : "Flagged for review"}${line.flag_reason ? ": " + line.flag_reason : ""} — click to edit`
          : "Flag this line (assumed / review)"}
        className={cls("inline-flex rounded p-0.5 transition-opacity",
          flagged ? "opacity-100" : "opacity-0 group-hover:opacity-60 hover:!opacity-100")}
        style={{ color: iconColor || "#9a958a" }}
      >
        <Flag size={12} />
      </button>
      {/* Body portal so the phase table can't clip it; flips up near the
          bottom of the viewport so the Flag button is always reachable. */}
      <AnchoredPortal anchorRef={btnRef} open={open} onDismiss={() => setOpen(false)}
        className="w-64 rounded-md border border-line bg-surface shadow-card p-3 text-left"
        style={{ fontSize: 12 }}
        onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setOpen(false); btnRef.current?.focus(); } }}
        data-testid={`line-flag-popover-${line.id}`}
      >
            <div className="col-head mb-2" style={{ fontSize: 10 }}>Flag line</div>
            <div className="flex gap-1.5 mb-2">
              {[["blind", "Assumed"], ["review", "Review"]].map(([t, l]) => (
                <button key={t} type="button" onClick={() => setType(t)}
                  data-testid={`line-flag-type-${t}-${line.id}`}
                  aria-pressed={type === t}
                  className="h-7 px-2.5 rounded-md border text-xs inline-flex items-center gap-1"
                  style={type === t ? { background: color(t), color: "#fff", borderColor: color(t) } : { color: color(t), borderColor: "var(--line, #e5e2da)" }}>
                  <Flag size={10} /> {l}
                </button>
              ))}
            </div>
            <textarea
              ref={reasonRef}
              value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder={type === "blind" ? "What info is missing? (optional)" : "Why review? (optional)"}
              data-testid={`line-flag-reason-${line.id}`}
              className="w-full rounded border border-line bg-surface px-2 py-1 text-ink" style={{ fontSize: 11.5 }}
            />
            <div className="flex justify-between items-center mt-2">
              {flagged
                ? <button onClick={doClear} data-testid={`line-flag-clear-${line.id}`} className="text-xs text-note-red hover:underline">Clear flag</button>
                : <span />}
              <button onClick={save} data-testid={`line-flag-save-${line.id}`}
                className="text-xs px-3 py-1 rounded-md text-white" style={{ background: color(type) }}>
                {flagged ? "Update" : "Flag"}
              </button>
            </div>
      </AnchoredPortal>
    </span>
  );
}

function LineRow({
  line, onUpdate, onRemove, subPhases, onMoveToSubPhase,
  onOpenRecipeDrawer, onSaveToLibrary, onUpdateLibraryFromLine,
  onActivateContainer, isActiveContainer, libraryDrift,
  labourRates = [], onRefreshFromLibrary,
}) {
  // Wrap update so that any change triggers override detection (when line has a recipe snapshot)
  const apply = (next) => onUpdate(applyOverrideDetection(next));
  // LT-20 — line-tier wastage override. Rebuilds material_rate + the
  // snapshot baseline together, so it is never mistaken for a manual
  // material_rate override.
  const setLineWastage = (pct) => apply(applyLineWastageOverride(line, pct));
  const quantityErrors = line.quantity_errors || [];
  const { user } = useAuth();
  // Phase 1.5 (item 7) — line flag popover state, openable from the ref-cell
  // icon OR the overflow menu. Metadata only → RAW onUpdate, never `apply`
  // (so flagging never trips pricing-override detection).
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagPreset, setFlagPreset] = useState(null);
  const applyFlag = (type, reason) => onUpdate({
    ...line, flag_type: type, flag_reason: (reason || "").trim() || null,
    flagged_at: new Date().toISOString(),
    flagged_by: user?.id || null, flagged_by_name: user?.name || user?.email || null,
  });
  const clearFlag = () => onUpdate({
    ...line, flag_type: null, flag_reason: null,
    flagged_at: null, flagged_by: null, flagged_by_name: null,
  });
  // Phase 1.5 (B/C) — jump-to-line focus wash (persistent lilac). Cleared when
  // the user engages with the row (dismiss / start editing).
  const { focusLineId, dismissFocus } = useContext(FocusLineContext);
  const isFocused = focusLineId === line.id;
  // LT-Estimate-UX-01 Section 3 — qty is intentionally not diffed. But
  // changing qty on a library line would normally leave `hours` (and each
  // additional_trades[i].hours) stale, spuriously tripping hours_override.
  // Fix: when qty changes AND that specific hours cell is NOT currently an
  // override (i.e. the estimator hasn't already customised the hours), auto-
  // rescale hours to `hours_per_unit * newQty` from the snapshot. Preserves
  // user-entered custom hours untouched, keeps normal qty edits clean.
  const set = (patch) => {
    let next = { ...line, ...patch };
    if ("qty" in patch && line.recipe_snapshot) {
      const snap = line.recipe_snapshot;
      const newQty = lineQty(next.qty);
      const r2 = (n) => Math.round(n * 100) / 100;
      if (snap.hours_per_unit != null && !line.hours_override) {
        next.hours = r2(Number(snap.hours_per_unit) * newQty);
      }
      if (Array.isArray(next.additional_trades) && Array.isArray(snap.additional_trades)) {
        next.additional_trades = next.additional_trades.map((t, i) => {
          if (i < snap.additional_trades.length && !t.hours_override) {
            return { ...t, hours: r2(Number(snap.additional_trades[i].hours_per_unit) * newQty) };
          }
          return t;
        });
      }
    }
    apply(next);
  };
  const onReapplyRecipe = () => apply(reapplyRecipe(line));

  // Secondary trades default to collapsed; expand on demand or auto-open while empty so
  // a freshly-added trade is immediately editable.
  const tradesCount = (line.additional_trades || []).length;
  const [showTrades, setShowTrades] = useState(false);

  // Per-line independent expand state for the inline recipe read view.
  // Each row tracks its own state so opening 1.01's recipe doesn't collapse 1.02's.
  const [showRecipe, setShowRecipe] = useState(false);
  const hasSnapshot = !!line.recipe_snapshot;
  // LT-Estimate-UX-01 Section 3 — snapshot baselines for per-cell tooltips and
  // revert buttons. Precomputed here rather than in each cell to avoid the
  // per-render function-call overhead in a 100+ line grid.
  const snap = line.recipe_snapshot || null;
  const qty = lineQty(line.qty);
  const baselineHours = snap && snap.hours_per_unit != null ? Number(snap.hours_per_unit) * qty : null;
  const baselineHourly = snap ? snap.hourly_rate : null;
  const baselineMat    = snap ? snap.material_rate : null;
  const baselinePlant  = snap ? snap.plant_rate : null;
  const baselineSubby  = snap ? snap.subby_rate : null;
  const baselineUnit   = snap ? (snap.unit || "") : "";

  // Per-cell revert callbacks — reset one field to its snapshot value. Only
  // wired when the snapshot has that baseline; otherwise onRevert=undefined
  // hides the revert affordance in NumCell/UnitCell.
  const revertHours       = snap && snap.hours_per_unit != null
    ? () => set({ hours: baselineHours }) : undefined;
  const revertHourly      = snap && snap.hourly_rate    != null ? () => set({ hourly_rate: snap.hourly_rate }) : undefined;
  const revertMaterial    = snap && snap.material_rate  != null ? () => set({ material_rate: snap.material_rate }) : undefined;
  const revertPlant       = snap && snap.plant_rate     != null ? () => set({ plant_rate: snap.plant_rate }) : undefined;
  const revertSubby       = snap && snap.subby_rate     != null ? () => set({ subby_rate: snap.subby_rate }) : undefined;
  const revertUnit        = snap && snap.unit                   ? () => set({ unit: snap.unit }) : undefined;

  // Polish §2 — drag-and-drop. Each line is a sortable item; its drag listeners
  // are attached to the GripVertical button so editing inputs don't accidentally
  // initiate a drag.
  const sortable = useSortable({ id: ID.line(line.id) });
  const dragStyle = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.4 : 1,
  };

  // LT-Estimate-UX-01 Section 3 — Row-level pricing override state is now
  // fully DERIVED from the per-cell diffs (no manual toggle). Only library
  // lines with a snapshot can be "edited"; ad-hoc lines are never amber.
  // `descriptionOnly` distinguishes a line whose ONLY change is the
  // description — that never turns the row amber (per user spec) but is
  // still tracked for Section 5 review.
  const isEditedLibraryLine = anyPricingOverride(line);
  const isLibraryLine = line.type === "library" && hasSnapshot;
  // LT-Estimate-UX-01 Section 4 — "estimated each use" line with no hours
  // entered. The row is legitimate-but-incomplete: the estimator must fill
  // hours before the tender can be sent. Never a bug (mode-2 lines start
  // empty by design), always a pre-send blocker.
  const needsHoursEntry = (
    line.labour_mode === "estimated_each_use" &&
    (line.hours == null || Number(line.hours) === 0)
  );

  // Section 3 — fire onActivateContainer whenever a click/focus happens
  // inside the row so the parent can flip the "active sub-phase" highlight
  // and re-target the context-aware footer buttons.
  const fireActivate = () => onActivateContainer?.();

  const addTrade = () => {
    onUpdate({
      ...line,
      additional_trades: [
        ...(line.additional_trades || []),
        { id: newId(), trade_name: "", hours: 0, hourly_rate: 0 }
      ]
    });
    setShowTrades(true);
  };

  return (
    <>
      <tr
        ref={sortable.setNodeRef}
        id={`line-${line.id}`}
        style={dragStyle}
        {...sortable.attributes}
        onMouseDownCapture={fireActivate}
        onFocusCapture={fireActivate}
        className={cls(
          "group border-b border-line-soft hover:bg-surface-2/40",
          isEditedLibraryLine && "bg-warning-soft/40",
          // LT-Estimate-UX-01 Section 4 — mode-2 line waiting for hours
          // gets a soft amber wash (distinct from the LIBRARY · EDITED
          // tint via a heavier left rule below).
          needsHoursEntry && !isEditedLibraryLine && "bg-warning-soft/30",
          // Phase 1.5 (item 7) — persistent flag-state wash + leading bar.
          // Background fill = state (never used for the transient focus ring).
          line.flag_type === "blind" && "flag-blind",
          line.flag_type === "review" && "flag-review",
          // Phase 1.5 (B/C) — persistent lilac focus wash (overrides the flag
          // wash; the flag's leading bar + icon still show through).
          isFocused && "flag-focus",
          sortable.isDragging && "shadow-card"
        )}
      >
        {/* Recipe expand chevron — far left, dedicated column */}
        <td
          className="px-1 py-2 align-top text-center"
          style={isActiveContainer ? { boxShadow: "inset 3px 0 0 #2F4F3E" } : undefined}
        >
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowRecipe((s) => !s)}
            data-testid={`recipe-toggle-${line.id}`}
            title={showRecipe ? "Hide recipe" : "Show recipe"}
            className={cls(
              "p-0.5 rounded text-ink-3 hover:text-accent hover:bg-accent-soft transition-colors",
              showRecipe && "text-accent bg-accent-soft"
            )}
          >
            {showRecipe ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        </td>
        <td className="px-1 py-2 align-top">
          <button
            type="button"
            tabIndex={-1}
            ref={sortable.setActivatorNodeRef}
            {...sortable.listeners}
            data-testid={`drag-handle-line-${line.id}`}
            title="Drag to reorder"
            className="cursor-grab active:cursor-grabbing text-ink-4 opacity-30 group-hover:opacity-90 transition-opacity hover:text-accent"
          >
            <GripVertical size={12} />
          </button>
        </td>
        <td className="px-2 py-2 align-top">
          <span className="font-mono tnum text-ink-3" style={{ fontSize: 11 }}>
            {line.ref || "—"}
          </span>
          <LineFlagPopover
            line={line}
            open={flagOpen}
            setOpen={setFlagOpen}
            preset={flagPreset}
            apply={applyFlag}
            clear={clearFlag}
          />
          {/* Item 3 — internal-note indicator. Sits AFTER the flag popover
              in the ref-cell so it never displaces existing flag layout. */}
          <InternalNoteLineGlyph lineId={line.id} description={line.description} />
        </td>
        <td className="px-1 py-2 align-top">
          <div className="flex items-start gap-1.5">
            {/* LT-Estimate-UX-01 Section 3 — the amber row tint and the
                LIBRARY \u00b7 EDITED badge together carry the row-level edit
                signal. The per-cell orange values and dots show WHAT changed.
                A third icon (previously a warning triangle here) was one
                signal too many and read like an error, so it was removed. */}
            {libraryDrift && (
              <span
                title={
                  "Library rate has changed since this line was added " +
                  `(now £${(libraryDrift.current_library_rate ?? 0).toFixed(2)}). ` +
                  "This line still uses its own snapshot — no automatic update."
                }
                aria-label="Library rate has changed since this line was added"
                data-testid={`library-drift-${line.id}`}
                className="flex-shrink-0 inline-flex items-center justify-center mt-1.5 cursor-help"
                style={{ color: "#5B6470" }}
              >
                <History size={12} />
              </span>
            )}
            <ArchivedLibraryMarker lineId={line.id} />
            <div className="flex-1 min-w-0">
              <TextCell value={line.description} onChange={(v) => set({ description: v })} placeholder="Describe the line item…" />
              <NoteCell value={line.note} onChange={(v) => set({ note: v })} lineId={line.id} />
            </div>
            {/* LT-Estimate-UX-01 Section 4 — mode-2 "estimated each use"
                indicator. Amber chip when hours are still blank; grey chip
                once the estimator has entered a value. Both cases keep
                the row identifiable as unmeasured labour throughout the
                tender lifetime. */}
            {line.labour_mode === "estimated_each_use" && (
              <span
                data-testid={`labour-mode-chip-${line.id}`}
                title={needsHoursEntry
                  ? "Estimated each use — enter hours to complete this line before sending"
                  : "Estimated each use — hours are a per-tender judgement, not a rate"}
                aria-label={needsHoursEntry ? "Line needs hours entered" : "Estimated each use"}
                className="flex-shrink-0 inline-flex items-center gap-1 mt-1 rounded font-mono"
                style={{
                  background: needsHoursEntry ? "#FEF3E2" : "#EEEBE3",
                  color: needsHoursEntry ? "#7A4C0A" : "#5B6470",
                  border: `1px solid ${needsHoursEntry ? "#F2DFB8" : "#DCD2BD"}`,
                  padding: "1px 5px", fontSize: 9, fontWeight: 600, letterSpacing: "0.06em",
                  whiteSpace: "nowrap",
                }}
              >
                {needsHoursEntry ? "NEEDS HOURS" : "EST. HOURS"}
              </span>
            )}
            {/* LT-Estimate-UX-01 Section 3 — description edited is TRACKED
                but NEVER turns the row amber. Show a quiet grey dot so it's
                visible on hover in the review flow (Section 5), separate from
                the pricing signal. */}
            {isLibraryLine && line.description_edited && (
              <span
                title={`Description edited. Was: “${line.recipe_snapshot?.description || ""}”. Description edits do not affect pricing.`}
                aria-label="Description edited (non-pricing)"
                data-testid={`description-edited-${line.id}`}
                className="flex-shrink-0 inline-flex items-center justify-center mt-1.5 cursor-help"
              >
                <span className="block rounded-full" style={{ width: 5, height: 5, background: "#94A3B8" }} />
              </span>
            )}
          </div>
        </td>
        <td className="px-2 py-2 align-top">
          <TypeBadge value={line.type || "adhoc"} overridden={isEditedLibraryLine} />
        </td>
        <td className="px-1 py-2 align-top">
          <div className="flex items-center gap-1.5">
            <TradeCell
              value={line.trade_name}
              labourRates={labourRates}
              onChange={(patch) => set(patch)}
              placeholder="Trade"
              testId={`cell-trade-${line.id}`}
            />
            {tradesCount > 0 && (
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowTrades((s) => !s)}
                title={showTrades ? "Hide other trades" : "Show other trades"}
                className="inline-flex items-center gap-0.5 rounded text-ink-3 hover:text-accent flex-shrink-0"
                style={{
                  background: "#EEF3EE",
                  border: "1px solid #D6E2D8",
                  padding: "1px 5px",
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#3A5A47",
                  letterSpacing: "0.02em"
                }}
              >
                {showTrades ? <ChevronDown size={9} /> : <ChevronRight size={9} />}
                +{tradesCount}
              </button>
            )}
          </div>
        </td>
        <td className="px-1 py-2 align-top">
          {/* Qty is intentionally NOT diffed (recipe is per-unit). LT-38 —
              a library line at qty 0 wears an amber ring until a quantity
              is entered. */}
          {line.type === "library" && Number(line.qty) === 0 ? (
            <div
              className="rounded-md ring-2 ring-amber-400/80 bg-amber-50"
              title="Enter a quantity — this line prices as nothing until you do"
              data-testid={`cell-qty-${line.id}-missing`}
            >
              <NumCell value={line.qty} onChange={(v) => set({ qty: v })} placeholder="qty?" testId={`cell-qty-${line.id}`} />
            </div>
          ) : (
            <NumCell value={line.qty} onChange={(v) => set({ qty: v })} placeholder="—" testId={`cell-qty-${line.id}`} />
          )}
        </td>
        <td className="px-1 py-2 align-top">
          <UnitCell
            value={line.unit}
            onChange={(v) => set({ unit: v })}
            override={!!line.unit_override}
            baselineValue={baselineUnit}
            onRevert={revertUnit}
          />
        </td>
        <GCell group="labour" align="right" className="px-1.5">
          <NumCell value={line.hours} onChange={(v) => set({ hours: v })}
            navCol="hours"
            override={line.hours_override}
            baselineValue={baselineHours}
            baselineSymbol="hours"
            onRevert={revertHours}
            testId={`cell-hours-${line.id}`} />
        </GCell>
        <GCell group="labour" align="right" className="px-1.5">
          <NumCell value={line.hourly_rate} onChange={(v) => set({ hourly_rate: v })}
            navCol="hourly_rate"
            override={line.hourly_rate_override}
            baselineValue={baselineHourly}
            baselineSymbol="money"
            onRevert={revertHourly}
            testId={`cell-hourly-rate-${line.id}`} />
        </GCell>
        <GCell group="material" align="right" className="px-1.5">
          <NumCell value={line.material_rate} onChange={(v) => set({ material_rate: v })}
            navCol="material_rate"
            override={line.material_rate_override}
            baselineValue={baselineMat}
            baselineSymbol="money"
            onRevert={revertMaterial}
            testId={`cell-material-rate-${line.id}`} />
          <div className="flex flex-col items-end">
            {quantityErrors.length > 0 && (
              <span
                className="inline-flex items-center gap-0.5 rounded px-1 h-4 mt-0.5 bg-[#FDE8E6] text-[#B42318] whitespace-nowrap"
                style={{ fontSize: 9.5, fontWeight: 600 }}
                title={quantityErrors.join("\n")}
                data-testid={`line-quantity-error-${line.id}`}
              >
                <AlertTriangle size={9} /> coverage missing
              </span>
            )}
            <LineWastageBadge line={line} onChange={setLineWastage} />
          </div>
        </GCell>
        <GCell group="material" align="right" className="px-2">
          <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>
            {line.material_total ? num(line.material_total) : "—"}
          </span>
        </GCell>
        <GCell group="plant" align="right" className="px-1.5">
          <NumCell value={line.plant_rate} onChange={(v) => set({ plant_rate: v })}
            navCol="plant_rate"
            override={line.plant_rate_override}
            baselineValue={baselinePlant}
            baselineSymbol="money"
            onRevert={revertPlant}
            testId={`cell-plant-rate-${line.id}`} />
        </GCell>
        <GCell group="plant" align="right" className="px-2">
          <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>
            {line.plant_total ? num(line.plant_total) : "—"}
          </span>
        </GCell>
        <GCell group="subby" align="right" className="px-1.5">
          <NumCell value={line.subby_rate} onChange={(v) => set({ subby_rate: v })}
            navCol="subby_rate"
            override={line.subby_rate_override}
            baselineValue={baselineSubby}
            baselineSymbol="money"
            onRevert={revertSubby}
            testId={`cell-subby-rate-${line.id}`} />
        </GCell>
        <GCell group="subby" align="right" className="px-2">
          <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>
            {line.subby_total ? num(line.subby_total) : "—"}
          </span>
        </GCell>
        <td className="px-2 py-2 align-top text-right" style={{ background: "#1A1A1A0A" }}>
          <span className="font-mono tnum text-ink" style={{ fontSize: 12 }}>
            {line.total_rate ? num(line.total_rate) : "—"}
          </span>
        </td>
        <td className="px-2 py-2 align-top text-right" style={{ background: "#1A1A1A0F" }}>
          <span className="font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 600 }}>
            {line.line_total ? num(line.line_total) : "—"}
          </span>
        </td>
        <td className="px-1 py-2 align-top text-right pr-2">
          <RowMenu line={line} onUpdate={onUpdate} onRemove={onRemove}
            subPhases={subPhases} onMoveToSubPhase={onMoveToSubPhase}
            onReapplyRecipe={onReapplyRecipe}
            onAddTrade={addTrade}
            onSaveToLibrary={() => onSaveToLibrary?.(line)}
            onUpdateLibraryFromLine={() => onUpdateLibraryFromLine?.(line)}
            onRefreshFromLibrary={() => onRefreshFromLibrary?.(line)}
            onOpenFlag={(type) => { setFlagPreset(type || null); setFlagOpen(true); }} />
        </td>
      </tr>

      {showTrades && tradesCount > 0 && (
        <SecondaryTradesRow
          trades={line.additional_trades}
          onUpdate={(t) => apply({ ...line, additional_trades: t })}
          snapshotTrades={snap ? (snap.additional_trades || []) : []}
          qty={qty}
          labourRates={labourRates}
        />
      )}

      {/* Phase 9 — inline recipe read view (collapsed by default, per-line state) */}
      {showRecipe && (
        <tr className="border-b border-line-soft">
          <td colSpan={19} className="p-0">
            <RecipeReadView
              line={line}
              hasLibraryLink={hasSnapshot && !!line.recipe_snapshot.library_item_id}
              onEditRecipe={() => onOpenRecipeDrawer?.(line)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/* ===================== library picker modal ===================== */
/* ===================== sub-phase header row ===================== */
function SubPhaseHeaderRow({ sp, onRename, onRemove, onAddLine, onAddFromLibrary, onActivateContainer, isActiveContainer }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(sp.name);

  // Polish §2 — sub-phase is sortable. Drag listeners go on a small grip
  // button so clicking the name still triggers rename mode.
  const sortable = useSortable({ id: ID.sub(sp.id) });
  const dragStyle = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.4 : 1,
  };

  // Section 3 — track click/focus inside the header row to flip the active
  // state on the parent.
  const fireActivate = () => onActivateContainer?.();

  return (
    <tr
      ref={sortable.setNodeRef}
      style={{ background: "#F5F3EE", ...dragStyle }}
      {...sortable.attributes}
      onMouseDownCapture={fireActivate}
      onFocusCapture={fireActivate}
      className={cls(
        "border-y border-line-soft group",
        sortable.isDragging && "shadow-card"
      )}
      data-testid={`sub-phase-row-${sp.id}`}
    >
      <td
        colSpan={19}
        className="px-3 py-2"
        style={isActiveContainer ? { boxShadow: "inset 3px 0 0 #2F4F3E" } : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              ref={sortable.setActivatorNodeRef}
              {...sortable.listeners}
              data-testid={`drag-handle-sub-${sp.id}`}
              title="Drag to reorder sub-phase"
              className="cursor-grab active:cursor-grabbing text-ink-4 opacity-30 group-hover:opacity-90 transition-opacity hover:text-accent"
            >
              <GripVertical size={11} />
            </button>
            <span className="col-head text-ink-2" style={{ fontWeight: 700 }}>SUB-PHASE</span>
            {editing ? (
              <input value={draft} onChange={(e) => setDraft(e.target.value)}
                onBlur={() => { setEditing(false); if (draft && draft !== sp.name) onRename(draft); }}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                autoFocus
                className="bg-bg border border-line rounded px-2 py-0.5 text-ink uppercase font-mono"
                style={{ fontSize: 11, letterSpacing: "0.06em" }} />
            ) : (
              <button onClick={() => setEditing(true)} className="col-head text-ink hover:text-accent" style={{ fontWeight: 700 }}>
                {sp.name}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={onAddFromLibrary} className="inline-flex items-center gap-1 text-accent hover:text-accent-deep" style={{ fontSize: 11, fontWeight: 600 }}>
              <Sparkles size={11} /> Library item
            </button>
            <button onClick={onAddLine} className="inline-flex items-center gap-1 text-ink-3 hover:text-accent" style={{ fontSize: 11 }}>
              <Plus size={11} /> Add blank
            </button>
            <button onClick={() => { if (window.confirm("Delete this sub-phase and its lines?")) onRemove(); }}
              className="text-ink-3 hover:text-note-red"><Trash2 size={11} /></button>
          </div>
        </div>
      </td>
    </tr>
  );
}

/* ===================== empty drop-zone rows (Polish §2) =====================
 * Render a thin droppable target row when a container is empty so a line/
 * sub-phase can be dragged into it. The container id matches the convention
 * used by `resolveLineDropTarget` / `resolveSubPhaseDropTarget` in
 * `lib/dnd.js`.
 */
function EmptyLinesDropRow({ containerId, label = "Drop a line here" }) {
  const { setNodeRef, isOver } = useDroppable({ id: containerId });
  return (
    <tr
      ref={setNodeRef}
      data-testid={`drop-zone-${containerId}`}
      style={{ background: isOver ? "#E8EFE9" : "transparent" }}
      className={cls(
        "border-y border-dashed transition-colors",
        isOver ? "border-accent" : "border-line-soft"
      )}
    >
      <td colSpan={19} className="px-3 py-3 text-center">
        <span className="text-ink-3 italic" style={{ fontSize: 11 }}>
          {isOver ? "Release to drop" : label}
        </span>
      </td>
    </tr>
  );
}

function EmptySubPhaseDropRow({ phaseId, label = "Drop a sub-phase here" }) {
  const containerId = ID.phaseSubphases(phaseId);
  const { setNodeRef, isOver } = useDroppable({ id: containerId });
  return (
    <tr
      ref={setNodeRef}
      data-testid={`drop-zone-${containerId}`}
      style={{ background: isOver ? "#E8EFE9" : "transparent" }}
      className={cls(
        "border-y border-dashed transition-colors",
        isOver ? "border-accent" : "border-line-soft"
      )}
    >
      <td colSpan={19} className="px-3 py-2 text-center">
        <span className="text-ink-3 italic" style={{ fontSize: 11 }}>
          {isOver ? "Release to drop" : label}
        </span>
      </td>
    </tr>
  );
}

/* ===================== estimate table ===================== */
function EstimateTable({ phase, onPhaseUpdate, onOpenLibraryPicker, onOpenRecipeDrawer, onSaveToLibrary, onUpdateLibraryFromLine, onRefreshFromLibrary, activeSubPhase, onActivateSubPhase, libraryDrift, labourRates = [] }) {
  const updateLine = (lineId, updater, subId = null) => {
    if (subId) {
      onPhaseUpdate({
        ...phase,
        sub_phases: phase.sub_phases.map((sp) =>
          sp.id !== subId ? sp : {
            ...sp,
            line_items: sp.line_items.map((l) => l.id === lineId ? updater(l) : l)
          })
      });
    } else {
      onPhaseUpdate({
        ...phase,
        line_items: phase.line_items.map((l) => l.id === lineId ? updater(l) : l)
      });
    }
  };
  const removeLine = (lineId, subId = null) => {
    if (subId) {
      onPhaseUpdate({
        ...phase,
        sub_phases: phase.sub_phases.map((sp) =>
          sp.id !== subId ? sp : { ...sp, line_items: sp.line_items.filter((l) => l.id !== lineId) })
      });
    } else {
      onPhaseUpdate({ ...phase, line_items: phase.line_items.filter((l) => l.id !== lineId) });
    }
  };
  const addBlankLine = (subId = null) => {
    const line = makeBlankLine();
    if (subId) {
      onPhaseUpdate({
        ...phase,
        sub_phases: phase.sub_phases.map((sp) =>
          sp.id !== subId ? sp : { ...sp, line_items: [...sp.line_items, line] })
      });
    } else {
      onPhaseUpdate({ ...phase, line_items: [...phase.line_items, line] });
    }
  };
  const addSubPhase = () => {
    const sp = makeBlankSubPhase("NEW SUB-PHASE");
    onPhaseUpdate({ ...phase, sub_phases: [...phase.sub_phases, sp] });
  };
  const renameSub = (subId, name) => {
    onPhaseUpdate({
      ...phase,
      sub_phases: phase.sub_phases.map((sp) => sp.id === subId ? { ...sp, name } : sp)
    });
  };
  const removeSub = (subId) =>
    onPhaseUpdate({ ...phase, sub_phases: phase.sub_phases.filter((sp) => sp.id !== subId) });

  // Move line between root <-> sub-phase
  const moveLine = (line, fromSubId, toSubId) => {
    let next = { ...phase };
    if (fromSubId) {
      next = {
        ...next,
        sub_phases: next.sub_phases.map((sp) =>
          sp.id !== fromSubId
            ? sp
            : { ...sp, line_items: sp.line_items.filter((l) => l.id !== line.id) }
        )
      };
    } else {
      next = { ...next, line_items: next.line_items.filter((l) => l.id !== line.id) };
    }
    if (toSubId) {
      next = {
        ...next,
        sub_phases: next.sub_phases.map((sp) =>
          sp.id !== toSubId
            ? sp
            : { ...sp, line_items: [...sp.line_items, line] }
        )
      };
    } else {
      next = { ...next, line_items: [...next.line_items, line] };
    }
    onPhaseUpdate(next);
  };

  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full" style={{ minWidth: 1500, borderCollapse: "separate", borderSpacing: 0 }}>
        <ColGroup />
        <thead>
          <tr>
            <th colSpan={6} style={{ background: "#FBFAF7" }} />
            <th colSpan={2} style={{ background: "#FBFAF7" }} />
            <GroupTh group="labour" colSpan={2} rounded="l">Labour</GroupTh>
            <GroupTh group="material" colSpan={2}>Material</GroupTh>
            <GroupTh group="plant" colSpan={2}>Plant</GroupTh>
            <GroupTh group="subby" colSpan={2}>Subby / PS</GroupTh>
            <GroupTh group="total" colSpan={2} rounded="r">Total</GroupTh>
            <th style={{ background: "#FBFAF7" }} />
          </tr>
          <tr>
            <th />
            <th />
            <SubTh>Ref</SubTh>
            <SubTh>Description</SubTh>
            <SubTh>Type</SubTh>
            <SubTh>Trade</SubTh>
            <SubTh align="right">Qty</SubTh>
            <SubTh align="right">Unit</SubTh>
            <SubTh group="labour" align="right">Hours</SubTh>
            <SubTh group="labour" align="right">£/hr</SubTh>
            <SubTh group="material" align="right">Rate</SubTh>
            <SubTh group="material" align="right">Total</SubTh>
            <SubTh group="plant" align="right">Rate</SubTh>
            <SubTh group="plant" align="right">Total</SubTh>
            <SubTh group="subby" align="right">Rate</SubTh>
            <SubTh group="subby" align="right">Total</SubTh>
            <SubTh align="right">Rate</SubTh>
            <SubTh align="right">Line £</SubTh>
            <th />
          </tr>
        </thead>
        <tbody>
          {/* Phase-root lines: their own SortableContext for cross-container DnD */}
          <SortableContext
            items={phase.line_items.map((l) => ID.line(l.id))}
            strategy={verticalListSortingStrategy}
          >
            {phase.line_items.map((line) => (
              <LineRow key={line.id} line={line}
                onUpdate={(l) => updateLine(line.id, () => l)}
                onRemove={() => removeLine(line.id)}
                subPhases={phase.sub_phases}
                onMoveToSubPhase={(toId) => moveLine(line, null, toId)}
                onOpenRecipeDrawer={onOpenRecipeDrawer}
                onSaveToLibrary={onSaveToLibrary}
                onUpdateLibraryFromLine={onUpdateLibraryFromLine}
                onRefreshFromLibrary={onRefreshFromLibrary}
                onActivateContainer={() => onActivateSubPhase?.({ phaseId: phase.id, subPhaseId: null })}
                isActiveContainer={false}
                libraryDrift={libraryDrift?.[line.id]}
                labourRates={labourRates}
              />
            ))}
          </SortableContext>
          {/* Empty drop target for the phase-root line list */}
          {phase.line_items.length === 0 && phase.sub_phases.length > 0 && (
            <EmptyLinesDropRow
              containerId={ID.phaseRootLines(phase.id)}
              label="Drop a line here (phase root)"
            />
          )}

          {/* Sub-phase headers: their own SortableContext */}
          <SortableContext
            items={phase.sub_phases.map((sp) => ID.sub(sp.id))}
            strategy={verticalListSortingStrategy}
          >
            {phase.sub_phases.map((sp) => {
              const isActive = activeSubPhase?.phaseId === phase.id
                && activeSubPhase?.subPhaseId === sp.id;
              const activate = () => onActivateSubPhase?.({ phaseId: phase.id, subPhaseId: sp.id });
              return (
                <React.Fragment key={sp.id}>
                  <SubPhaseHeaderRow sp={sp}
                    onRename={(name) => renameSub(sp.id, name)}
                    onRemove={() => removeSub(sp.id)}
                    onAddLine={() => addBlankLine(sp.id)}
                    onAddFromLibrary={() => onOpenLibraryPicker(phase.id, sp.id)}
                    onActivateContainer={activate}
                    isActiveContainer={isActive}
                  />
                  {/* Per-sub-phase line SortableContext */}
                  <SortableContext
                    items={sp.line_items.map((l) => ID.line(l.id))}
                    strategy={verticalListSortingStrategy}
                  >
                    {sp.line_items.map((line) => (
                      <LineRow key={line.id} line={line}
                        onUpdate={(l) => updateLine(line.id, () => l, sp.id)}
                        onRemove={() => removeLine(line.id, sp.id)}
                        subPhases={phase.sub_phases.filter((x) => x.id !== sp.id)}
                        onMoveToSubPhase={(toId) => moveLine(line, sp.id, toId)}
                        onOpenRecipeDrawer={onOpenRecipeDrawer}
                        onSaveToLibrary={onSaveToLibrary}
                        onUpdateLibraryFromLine={onUpdateLibraryFromLine}
                        onRefreshFromLibrary={onRefreshFromLibrary}
                        onActivateContainer={activate}
                        isActiveContainer={isActive}
                        libraryDrift={libraryDrift?.[line.id]}
                        labourRates={labourRates}
                      />
                    ))}
                  </SortableContext>
                  {/* Empty sub-phase line drop target */}
                  {sp.line_items.length === 0 && (
                    <EmptyLinesDropRow
                      containerId={ID.subphaseLines(sp.id)}
                      label="Drop a line into this sub-phase"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </SortableContext>
          {/* Empty drop target for the sub-phase list */}
          {phase.sub_phases.length === 0 && (
            <EmptySubPhaseDropRow phaseId={phase.id} />
          )}
          {(() => {
            // Section 3 — context-aware footer buttons. When the user has
            // recently clicked inside one of THIS phase's sub-phases, the
            // "Add blank line" / "From library" buttons re-target that
            // sub-phase and the labels show the destination. Otherwise they
            // fall back to the phase root (current behaviour).
            const targetSub = activeSubPhase?.phaseId === phase.id
              && activeSubPhase?.subPhaseId
              ? phase.sub_phases.find((sp) => sp.id === activeSubPhase.subPhaseId)
              : null;
            const subId = targetSub?.id || null;
            const subName = targetSub?.name || null;
            return (
              <tr>
                <td colSpan={19} className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onOpenLibraryPicker(phase.id, subId)}
                      data-testid={`footer-from-library-${phase.id}`}
                      className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-accent text-white hover:bg-accent-deep transition-colors"
                      style={{ fontSize: 12, fontWeight: 600 }}
                    >
                      <Sparkles size={12} />
                      <span>Library item</span>
                      {subName && (
                        <span
                          className="font-mono uppercase ml-1 px-1.5 py-0.5 rounded"
                          style={{
                            background: "rgba(255, 255, 255, 0.22)",
                            color: "#FFFFFF",
                            fontSize: 10,
                            letterSpacing: "0.04em",
                            fontWeight: 700,
                          }}
                        >
                          to {subName}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => addBlankLine(subId)}
                      data-testid={`footer-add-blank-line-${phase.id}`}
                      className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md border border-line bg-surface text-ink-2 hover:bg-accent-soft hover:text-accent hover:border-accent transition-colors"
                      style={{ fontSize: 12 }}
                    >
                      <Plus size={12} />
                      <span>Add blank line</span>
                      {subName && (
                        <span
                          className="font-mono uppercase ml-1 px-1.5 py-0.5 rounded"
                          style={{
                            background: "rgba(47, 79, 62, 0.12)",
                            color: "#2F4F3E",
                            fontSize: 10,
                            letterSpacing: "0.04em",
                            fontWeight: 700,
                          }}
                        >
                          to {subName}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={addSubPhase}
                      data-testid={`footer-add-sub-phase-${phase.id}`}
                      className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2 transition-colors"
                      style={{ fontSize: 12 }}
                    >
                      <ChevronRight size={12} /> Add sub-phase
                    </button>
                  </div>
                </td>
              </tr>
            );
          })()}
        </tbody>
      </table>
    </div>
  );
}

/* ===================== phase footer ===================== */
function PhaseFooter({ phase }) {
  const item = (label, value) => (
    <div className="flex flex-col items-end">
      <span className="col-head" style={{ fontSize: 9 }}>{label}</span>
      <span className="font-mono tnum text-ink mt-0.5" style={{ fontSize: 12.5, fontWeight: 600 }}>{value}</span>
    </div>
  );
  return (
    <div
      className="flex items-center justify-end gap-7 px-4 py-3 border-t-2"
      style={{
        // Slightly darker, more saturated warm sand than the rest of the
        // phase card so the totals band reads as the closing weight of the
        // phase. The 2px sage-tinted top border + small inset shadow on top
        // gives a clear "footer of section" cue without making the band loud.
        background: "#E8E2D2",
        borderTopColor: "#C9BFA8",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)",
      }}
    >
      {item("Hours", `${hrs(phase.hours_total || 0)}h`)}
      {item("Materials", gbp(phase.materials_total || 0))}
      {item("Labour", gbp(phase.labour_total || 0))}
      {item("Plant", gbp(phase.plant_total || 0))}
      {item("Subby", gbp(phase.subby_total || 0))}
      <div className="h-8 w-px" style={{ background: "#C9BFA8" }} />
      <div className="flex flex-col items-end">
        <span className="col-head" style={{ fontSize: 9 }}>Phase total</span>
        <span className="font-mono tnum text-ink mt-0.5" style={{ fontSize: 14, fontWeight: 700 }}>
          {gbp(phase.phase_total || 0)}
        </span>
      </div>
    </div>
  );
}

/* ===================== phase card ===================== */
const PRELIM_CAT_ORDER = ["scaffolding", "welfare", "management", "sundries"];
const PRELIM_CAT_LABEL = {
  scaffolding: "Scaffolding",
  welfare: "Welfare Facilities",
  management: "Project Management & Supervision",
  sundries: "Sundries (PPE, H&S, Protection, Skips & Waste, Cleaning, etc.)",
};

/**
 * LT-42 — read-only estimate phase for Prelims / Provisional Sums. Baked into
 * every estimate; looks like a normal phase but its line items are pulled from
 * the Prelims / Pro Sums tab (single source of truth) and can't be edited here.
 * Prelims pulls the 4 client-facing CATEGORY totals (the same grouping shown on
 * client documents), not the internal line-by-line build-up.
 */
function SystemPhaseCard({ tenderId, tender, phase }) {
  const isPrelims = phase.system_phase_type === "prelims";
  const tabKey = isPrelims ? "prelims" : "provsums";
  const tabLabel = isPrelims ? "Prelims" : "Pro Sums";
  const total = isPrelims ? (tender.prelims_total || 0) : (tender.provsums_total || 0);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [, setSearch] = useSearchParams();

  useEffect(() => {
    if (!open || rows) return;
    let alive = true;
    api.get(`/tenders/${tenderId}/${tabKey}`)
      .then((r) => {
        if (!alive) return;
        let mapped;
        if (isPrelims) {
          const ct = r.data.summary?.client_category_totals || {};
          mapped = PRELIM_CAT_ORDER
            .filter((c) => (ct[c] || 0) !== 0)
            .map((c) => ({ id: c, name: PRELIM_CAT_LABEL[c], value: ct[c] || 0 }));
        } else {
          mapped = (r.data.items || []).map((i) => ({ id: i.id, name: i.description || "Provisional sum", value: i.confirmed_value != null ? i.confirmed_value : (i.value || 0), confirmed: i.confirmed_value != null }));
        }
        setRows(mapped);
      })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [open, rows, tenderId, tabKey, isPrelims]);

  return (
    <div
      data-testid={`system-phase-${tabKey}`}
      className="bg-surface border border-line rounded-lg overflow-hidden mb-5"
      style={{ boxShadow: "0 1px 0 rgba(0,0,0,0.02), 0 6px 12px -6px rgba(58, 50, 32, 0.10), 0 2px 4px -2px rgba(58, 50, 32, 0.06)" }}
    >
      <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-2/30 transition-colors">
        <div className="flex items-center gap-3 flex-1">
          <span className="w-[14px]" />
          <button onClick={() => setOpen((x) => !x)} className="text-ink-2" data-testid={`system-phase-${tabKey}-toggle`}>
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <span className="font-display text-ink" style={{ fontSize: 16, fontWeight: 600 }}>{phase.name}</span>
          <span className="font-mono uppercase text-ink-3" style={{ fontSize: 9, letterSpacing: "0.06em", fontWeight: 700, padding: "1px 6px", border: "1px solid var(--line)", borderRadius: 3 }}>
            From {tabLabel} tab
          </span>
        </div>
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => setSearch({ tab: tabKey }, { replace: true })} className="text-accent hover:underline" style={{ fontSize: 11.5 }} data-testid={`system-phase-${tabKey}-manage`}>
            Manage on {tabLabel} tab →
          </button>
          <span className="font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 600 }}>{gbp(total)}</span>
        </div>
      </div>
      {open && (
        <div className="border-t border-line px-4 py-3" data-testid={`system-phase-${tabKey}-body`}>
          {rows === null ? (
            <div className="text-ink-3 py-2" style={{ fontSize: 12 }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-ink-3 py-2" style={{ fontSize: 12 }}>
              No {tabLabel} lines yet — add them on the <button type="button" className="text-accent hover:underline" onClick={() => setSearch({ tab: tabKey }, { replace: true })}>{tabLabel} tab</button>.
            </div>
          ) : (
            <table className="w-full" style={{ fontSize: 12.5 }}>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className={i !== rows.length - 1 ? "border-b border-line-soft" : ""} data-testid={`system-phase-${tabKey}-row-${i}`}>
                    <td className="py-1.5 pr-3 text-ink-2">
                      {r.name}
                      {r.confirmed ? (
                        <span
                          className="ml-2 inline-flex items-center rounded"
                          style={{ fontSize: 9, letterSpacing: "0.05em", fontWeight: 600, padding: "1px 6px", background: "#E8EFE9", color: "#2F4F3E", border: "1px solid #C9D9CC" }}
                          data-testid={`system-phase-${tabKey}-confirmed-${i}`}
                        >
                          CONFIRMED
                        </span>
                      ) : null}
                    </td>
                    <td className="py-1.5 text-right font-mono tnum text-ink" style={{ width: 120 }}>{gbp(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="text-ink-4 mt-2" style={{ fontSize: 10.5 }}>These lines are managed on the {tabLabel} tab and pull through automatically — they can't be edited here.</div>
        </div>
      )}
    </div>
  );
}

function PhaseCard({ phase, onPhaseUpdate, onRemove, onMoveUp, onMoveDown, onOpenLibraryPicker, onOpenRecipeDrawer, onSaveToLibrary, onUpdateLibraryFromLine, onRefreshFromLibrary, activeSubPhase, onActivateSubPhase, libraryDrift, labourRates = [] }) {
  const [open, setOpen] = useState(phase.line_count === 0 ? true : !phase.is_collapsed);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(phase.name);

  useEffect(() => { setDraft(phase.name); }, [phase.name]);

  // Polish §2 — phase is sortable. Drag listeners attach to the existing
  // GripVertical on the phase header. Other header controls (collapse,
  // rename, move-up/down, delete) remain clickable.
  const sortable = useSortable({ id: ID.phase(phase.id) });
  const dragStyle = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        ...dragStyle,
        // Soft drop-shadow under the phase card emphasises it as a closed
        // container — combined with the warmer phase-totals band and the
        // bigger inter-card gap, this gives a clear visual end-of-phase break
        // without darkening the overall theme.
        boxShadow:
          "0 1px 0 rgba(0,0,0,0.02), 0 6px 12px -6px rgba(58, 50, 32, 0.10), 0 2px 4px -2px rgba(58, 50, 32, 0.06)",
      }}
      {...sortable.attributes}
      data-testid={`phase-card-${phase.id}`}
      className={cls(
        "bg-surface border border-line rounded-lg overflow-hidden mb-5",
        sortable.isDragging && "ring-2 ring-accent/40"
      )}
    >
      <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-2/30 transition-colors">
        <div className="flex items-center gap-3 flex-1">
          <button
            type="button"
            ref={sortable.setActivatorNodeRef}
            {...sortable.listeners}
            data-testid={`drag-handle-phase-${phase.id}`}
            title="Drag to reorder phase"
            className="cursor-grab active:cursor-grabbing text-ink-4 hover:text-accent"
          >
            <GripVertical size={14} />
          </button>
          <button onClick={() => setOpen((x) => !x)} className="text-ink-2">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          {editing ? (
            <input value={draft} onChange={(e) => setDraft(e.target.value)}
              onBlur={() => { setEditing(false); if (draft && draft !== phase.name) onPhaseUpdate({ ...phase, name: draft }); }}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              autoFocus
              className="font-display text-ink bg-bg border border-line rounded px-2 py-0.5"
              style={{ fontSize: 16, fontWeight: 600 }} />
          ) : (
            <button onClick={() => setEditing(true)}
              className="font-display text-ink text-left hover:text-accent"
              style={{ fontSize: 16, fontWeight: 600 }}>
              {phase.name}
            </button>
          )}
          {/* Item 3 — phase-scoped internal-note indicator. */}
          <InternalNotePhaseGlyph phaseId={phase.id} phaseName={phase.name} />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-ink-3" style={{ fontSize: 11.5 }}>
            {phase.line_count || 0} item{phase.line_count === 1 ? "" : "s"}
            {phase.sub_phases?.length ? ` · ${phase.sub_phases.length} sub-phase${phase.sub_phases.length === 1 ? "" : "s"}` : ""}
          </span>
          <span className="font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
            {gbp(phase.phase_total || 0)}
          </span>
          <div className="flex items-center gap-0.5">
            <button onClick={onMoveUp} className="h-6 w-6 rounded text-ink-3 hover:bg-surface-2 hover:text-ink"><ChevronRight size={12} className="mx-auto rotate-[-90deg]" /></button>
            <button onClick={onMoveDown} className="h-6 w-6 rounded text-ink-3 hover:bg-surface-2 hover:text-ink"><ChevronRight size={12} className="mx-auto rotate-90" /></button>
            <button onClick={() => { if (window.confirm("Remove this phase and all its line items?")) onRemove(); }}
              className="h-6 w-6 rounded text-ink-3 hover:bg-surface-2 hover:text-note-red ml-1">
              <Trash2 size={11} className="mx-auto" />
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="border-t border-line">
          <EstimateTable phase={phase} onPhaseUpdate={onPhaseUpdate} onOpenLibraryPicker={onOpenLibraryPicker} onOpenRecipeDrawer={onOpenRecipeDrawer} onSaveToLibrary={onSaveToLibrary} onUpdateLibraryFromLine={onUpdateLibraryFromLine} onRefreshFromLibrary={onRefreshFromLibrary} activeSubPhase={activeSubPhase} onActivateSubPhase={onActivateSubPhase} libraryDrift={libraryDrift} labourRates={labourRates} />
          <PhaseFooter phase={phase} />
        </div>
      )}
    </div>
  );
}

/* ===================== modals ===================== */
function ApplyTemplateModal({ open, onClose, onApply, templates }) {
  const { labelFor: jobTypeLabelFor } = useJobTypeLibrary();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-start justify-center pt-16 px-4">
      <div className="w-full max-w-[560px] bg-surface border border-line rounded-lg shadow-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h2 className="font-display text-ink" style={{ fontSize: 16, fontWeight: 600 }}>Apply phase template</h2>
          <button onClick={onClose} className="text-ink-3 hover:text-ink"><X size={16} /></button>
        </div>
        <div className="p-3 max-h-[60vh] overflow-auto">
          {(templates || []).length === 0 ? (
            <div className="text-center py-10 text-ink-3" style={{ fontSize: 12.5 }}>
              No templates yet — create one from <a href="/templates" className="text-accent">Templates</a>.
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {templates.map((t) => (
                <button key={t.id} onClick={() => onApply(t)}
                  className="text-left px-3 py-2.5 rounded-md border border-line bg-surface hover:bg-surface-2 hover:border-accent transition-colors">
                  <div className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>{t.name}</div>
                  <div className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
                    {t.job_type ? `${jobTypeLabelFor(t.job_type)} · ` : ""}{(t.phases || []).length} phases
                    {t.description ? ` · ${t.description}` : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AddPhaseModal({ open, onClose, onAdd, masterPhases, existingNames }) {
  const [custom, setCustom] = useState("");
  if (!open) return null;
  const available = (masterPhases || []).filter((p) => !existingNames.has(p.name));
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-start justify-center pt-16 px-4">
      <div className="w-full max-w-[520px] bg-surface border border-line rounded-lg shadow-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h2 className="font-display text-ink" style={{ fontSize: 16, fontWeight: 600 }}>Add phase</h2>
          <button onClick={onClose} className="text-ink-3 hover:text-ink"><X size={16} /></button>
        </div>
        <div className="p-5">
          <Field label="From master phase list">
            <div className="flex flex-wrap gap-1.5 mt-1">
              {available.length === 0 ? (
                <span className="text-ink-3" style={{ fontSize: 12 }}>All master phases already added.</span>
              ) : available.map((p) => (
                <button key={p.id} onClick={() => onAdd(p.name)}
                  className="px-2.5 py-1 rounded-md border border-line bg-surface text-ink-2 hover:bg-accent-soft hover:text-accent hover:border-accent transition-colors"
                  style={{ fontSize: 12 }}>
                  {p.name}
                </button>
              ))}
            </div>
          </Field>
          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-line" />
            <span className="col-head">Or custom</span>
            <div className="h-px flex-1 bg-line" />
          </div>
          <Field label="Custom phase name">
            <Input value={custom} onChange={setCustom} placeholder="e.g. Mechanical & Electrical" />
          </Field>
          <div className="mt-4 flex justify-end gap-2">
            <QuietButton onClick={onClose}>Cancel</QuietButton>
            <PrimaryButton disabled={!custom.trim()} onClick={() => { if (custom.trim()) { onAdd(custom.trim()); setCustom(""); } }}>
              Add custom phase
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===================== bottom totals strip ===================== */
function TotalsStrip({ totals, prelims, provsums, oapPct, onPrelimsChange, onOapPctChange, status, lastSaved }) {
  return (
    <div className="sticky bottom-0 bg-surface border-t border-line shadow-[0_-1px_2px_rgba(26,26,26,0.04)]" style={{ zIndex: 30 }}>
      <div className="app-container flex items-center justify-between gap-6 py-3">
        <div className="flex items-center gap-5">
          <div className="flex flex-col">
            <span className="col-head" style={{ fontSize: 9 }}>Cost basis (Lux copy)</span>
            <span className="font-mono tnum text-ink mt-0.5" style={{ fontSize: 14, fontWeight: 600 }}>
              {gbp(totals.cost_basis || 0)}
            </span>
          </div>
          <ChevronRight size={14} className="text-ink-4" />
          <div className="flex flex-col" data-testid="totals-prelims-readonly">
            <span className="col-head" style={{ fontSize: 9 }}>+ Prelims</span>
            <div className="flex items-center gap-1 mt-0.5">
              <span
                className="font-mono tnum text-ink"
                style={{ fontSize: 13, fontWeight: 600 }}
                title="Set on the Prelims tab — this figure is read-only here."
              >
                {gbp(prelims || 0)}
              </span>
              <span
                className="font-mono uppercase text-ink-3 ml-1"
                style={{ fontSize: 9, letterSpacing: "0.06em", fontWeight: 700, padding: "1px 5px", border: "1px solid var(--line)", borderRadius: 3 }}
                title="Edit on the Prelims tab"
              >
                Prelims tab
              </span>
            </div>
          </div>
          <ChevronRight size={14} className="text-ink-4" />
          <div className="flex flex-col" data-testid="totals-provsums-readonly">
            <span className="col-head" style={{ fontSize: 9 }}>+ Pro Sums</span>
            <div className="flex items-center gap-1 mt-0.5">
              <span
                className="font-mono tnum text-ink"
                style={{ fontSize: 13, fontWeight: 600 }}
                title="Set on the Pro Sums tab — this figure is read-only here."
              >
                {gbp(provsums || 0)}
              </span>
              <span
                className="font-mono uppercase text-ink-3 ml-1"
                style={{ fontSize: 9, letterSpacing: "0.06em", fontWeight: 700, padding: "1px 5px", border: "1px solid var(--line)", borderRadius: 3 }}
                title="Edit on the Pro Sums tab"
              >
                Pro Sums tab
              </span>
            </div>
          </div>
          <ChevronRight size={14} className="text-ink-4" />
          <div className="flex flex-col">
            <span className="col-head" style={{ fontSize: 9 }}>+ O&P</span>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
                {num(oapPct ?? 0, 1)}%
              </span>
              <span className="font-mono tnum text-ink-2 ml-1" style={{ fontSize: 12 }}>
                {gbp(totals.oap_total || 0)}
              </span>
              <span
                className="font-mono uppercase text-ink-3 ml-1"
                style={{ fontSize: 9, letterSpacing: "0.06em", fontWeight: 700, padding: "1px 5px", border: "1px solid var(--line)", borderRadius: 3 }}
                title="Edit on the Adjudication tab"
              >
                Adjudication tab
              </span>
            </div>
          </div>
          <ChevronRight size={14} className="text-ink-4" />
          <div className="flex flex-col">
            <span className="col-head" style={{ fontSize: 9 }}>Client total</span>
            <span className="font-mono tnum mt-0.5" style={{ fontSize: 17, fontWeight: 700, color: "#2F4F3E" }}>
              {gbp(totals.client_total || 0)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SaveBadge status={status} lastSaved={lastSaved} />
        </div>
      </div>
    </div>
  );
}

/* ===================== empty state ===================== */
function EmptyEstimate({ onApplyTemplate, onAddPhase }) {
  return (
    <Card className="mt-4">
      <div className="flex flex-col items-center justify-center text-center py-14 px-6">
        <div className="w-12 h-12 rounded-full bg-surface-2 flex items-center justify-center text-ink-3 mb-3">
          <Sparkles size={20} />
        </div>
        <h3 className="font-display text-ink" style={{ fontSize: 18, fontWeight: 600 }}>Build your estimate</h3>
        <p className="text-ink-3 mt-1.5" style={{ fontSize: 12.5, maxWidth: 460 }}>
          Apply a phase template to pre-load a structure, or start from scratch by adding individual phases.
        </p>
        <div className="mt-5 flex items-center gap-2">
          <PrimaryButton icon={FileText} onClick={onApplyTemplate}>Apply phase template</PrimaryButton>
          <QuietButton icon={Plus} onClick={onAddPhase}>Add phase manually</QuietButton>
        </div>
      </div>
    </Card>
  );
}

/* ===================== main ===================== */
export default function TenderEstimate({ tender, onTenderUpdate }) {
  const [phases, setPhases] = useState(tender.phases || []);
  // Phase 1.5 (B) — deep-link from the dashboard/flagged views. ?line=<id>
  // scrolls to the row and applies a PERSISTENT lilac focus wash (React state
  // so it survives re-renders). No timer. Cleared on edit/dismiss (see
  // LineRow → FocusLineContext). The URL param is stripped so a reload does
  // NOT re-apply it.
  const [focusLineId, setFocusLineId] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lineId = params.get("line");
    if (!lineId) return;
    setFocusLineId(lineId);
    params.delete("line");
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, []);
  useEffect(() => {
    if (!focusLineId) return;
    const t = setTimeout(() => {
      document.getElementById(`line-${focusLineId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    return () => clearTimeout(t);
  }, [focusLineId]);
  // Phase 1.5 (B/C) — jump-to-line focus wash persists through clicks/scroll/
  // expand/edit. It clears only when a successful save COMPLETES for the
  // focused line specifically. A save on any other line must not clear it.
  //
  // Detection: on focus-set we snapshot the focused line's serialised state
  // (focusBaselineRef). Every subsequent phases update compares the focused
  // line to that baseline — if drifted, flip focusEditedRef true. When
  // flushSave succeeds and focusEditedRef is true, clear the focus and reset
  // baseline to the server-authoritative post-save state.
  //
  // No timer, no document mousedown listener. Explicit dismiss remains
  // available via focusCtx.dismissFocus. URL param is still stripped so a
  // reload cannot resurrect the wash.
  const focusBaselineRef = useRef(null);
  const focusEditedRef = useRef(false);
  const findFocusedLine = useCallback((phasesArg) => {
    if (!focusLineId || !Array.isArray(phasesArg)) return null;
    for (const p of phasesArg) {
      for (const li of (p.line_items || [])) if (li.id === focusLineId) return li;
      for (const sp of (p.sub_phases || [])) {
        for (const li of (sp.line_items || [])) if (li.id === focusLineId) return li;
      }
    }
    return null;
  }, [focusLineId]);
  useEffect(() => {
    if (!focusLineId) {
      focusBaselineRef.current = null;
      focusEditedRef.current = false;
      return;
    }
    focusBaselineRef.current = JSON.stringify(findFocusedLine(phases));
    focusEditedRef.current = false;
    // Intentional — snapshot only when focus changes, not on every phases tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusLineId]);
  const focusCtx = useMemo(
    () => ({ focusLineId, dismissFocus: () => setFocusLineId(null) }),
    [focusLineId]
  );

  // Item 3 — Internal notes: counts + shared popover state. Counts arrive
  // via the additive `internal_note_counts` field on GET /api/tenders/{id}
  // (empty object when absent — legacy safe). Reload from the notes list
  // endpoint after every mutation so glyphs update without a full tender
  // refetch.
  const [internalCounts, setInternalCounts] = useState(
    tender.internal_note_counts || { tender_level: 0, by_phase: {}, by_line: {} }
  );
  useEffect(() => {
    setInternalCounts(tender.internal_note_counts || { tender_level: 0, by_phase: {}, by_line: {} });
  }, [tender.internal_note_counts]);
  const [internalPopover, setInternalPopover] = useState(null); // {scope, targetId, targetLabel}
  const reloadInternalCounts = useCallback(async () => {
    try {
      const r = await api.get(`/tenders/${tender.id}/internal-notes`);
      if (r.data?.counts) setInternalCounts(r.data.counts);
    } catch (e) { /* silent — non-critical UI accessory */ }
  }, [tender.id]);
  const internalCtx = useMemo(() => ({
    tenderId: tender.id,
    counts: internalCounts,
    open: (scope, targetId, targetLabel) =>
      setInternalPopover({ scope, targetId, targetLabel }),
  }), [tender.id, internalCounts]);

  const [prelims, setPrelims] = useState(tender.prelims_total || 0);
  const [oapPct, setOapPct] = useState(tender.oap_pct == null ? 22 : tender.oap_pct);
  const [status, setStatus] = useState("saved");
  const [lastSaved, setLastSaved] = useState(null);
  // Prelims tab is now the source of truth for `prelims_total`. Keep the local
  // mirror in sync whenever the parent's tender prop refreshes (e.g. after
  // user edits a line on the Prelims tab) so the read-only +Prelims figure in
  // the totals strip never drifts.
  useEffect(() => {
    const next = tender.prelims_total || 0;
    setPrelims((cur) => (cur === next ? cur : next));
  }, [tender.prelims_total]);
  // Pro Sums tab is the source of truth for `provsums_total`; mirror it so the
  // read-only +Pro Sums figure and the pulled-through Provisional Sums phase
  // stay in sync.
  const [provsums, setProvsums] = useState(tender.provsums_total || 0);
  useEffect(() => {
    const next = tender.provsums_total || 0;
    setProvsums((cur) => (cur === next ? cur : next));
  }, [tender.provsums_total]);
  const [openTemplate, setOpenTemplate] = useState(false);
  const [openAddPhase, setOpenAddPhase] = useState(false);
  const [openLibraryPicker, setOpenLibraryPicker] = useState(null); // null | { phase_id, sub_phase_id }
  const [openShortcuts, setOpenShortcuts] = useState(false);   // LT-Kbd-Nav
  const [templates, setTemplates] = useState([]);
  const [masterPhases, setMasterPhases] = useState([]);
  const [libraryItems, setLibraryItems] = useState([]);
  const [labourRates, setLabourRates] = useState([]);
  const [materialsList, setMaterialsList] = useState([]);
  const [allExpanded, setAllExpanded] = useState(false);

  // Phase 9 — per-line recipe drawer state. Holds the line currently being
  // edited (so the drawer can read its snapshot) plus the path through phases
  // (used when the drawer save bubbles back to the right line).
  const [recipeDrawer, setRecipeDrawer] = useState(null); // null | { line, phaseId, subPhaseId }

  // Section 4 (brought forward) — save-as-new-library-item modal triggered
  // from the row-menu dropdown on any line. Independent of the drawer flow
  // (the drawer has its own footer button that uses live drawer arrays).
  const [saveToLibLine, setSaveToLibLine] = useState(null);

  // LT-Estimate-UX-01 Section 3 — "Update library item with these values…"
  // dialog. Only openable from an already-edited library line via the row
  // menu. Reuses `POST /api/library/{id}/push-from-tender` with optional
  // description + unit push toggles.
  const [updateLibFromLine, setUpdateLibFromLine] = useState(null);
  // LT-20 — per-line "Refresh from library" confirm dialog.
  const [refreshLine, setRefreshLine] = useState(null);
  const [openBulkRefresh, setOpenBulkRefresh] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Section 3 — Active sub-phase tracking for context-aware footer.
  // null  = no active sub-phase (footer falls back to phase root)
  // { phaseId, subPhaseId: null } = user clicked in phase root (acts like null
  //                                  for the footer, but lets us clear the
  //                                  highlight on the previously-active sub-phase)
  // { phaseId, subPhaseId } = user clicked in a sub-phase header / line
  // Session-only, resets on reload (per user spec: option a).
  const [activeSubPhase, setActiveSubPhase] = useState(null);

  // LT-28 §2.3 — informational drift indicator. Map: line_id -> drift row.
  // Populated by GET /api/tenders/{id}/library-drift on mount + whenever
  // the phases change materially (i.e. after any save). The indicator is
  // read-only; nothing is auto-updated.
  const [libraryDrift, setLibraryDrift] = useState({});
  // LT-21a §5 — line_id -> drift row for lines whose library item is archived.
  const [archivedRefs, setArchivedRefs] = useState({});
  const loadLibraryDrift = useCallback(async () => {
    if (!tender?.id) return;
    try {
      const r = await api.get(`/tenders/${tender.id}/library-drift`);
      const map = {};
      for (const row of r.data?.drift || []) {
        map[row.line_id] = row;
      }
      setLibraryDrift(map);
      const arch = {};
      for (const row of r.data?.lines || []) {
        if (row.library_item_archived) arch[row.line_id] = row;
      }
      setArchivedRefs(arch);
    } catch {
      setLibraryDrift({});
      setArchivedRefs({});
    }
  }, [tender?.id]);
  useEffect(() => { loadLibraryDrift(); }, [loadLibraryDrift]);

  const computed = useMemo(
    () => computeEstimate(phases, prelims, oapPct, provsums),
    [phases, prelims, oapPct, provsums]
  );
  // LT-42 — always show normal phases first, then Pro Sums, then Prelims at the
  // very bottom (regardless of stored sequence). Reorders display only.
  const orderedPhases = useMemo(() => {
    const ps = computed.phases || [];
    const normal = ps.filter((p) => !p.is_system_phase);
    const prov = ps.find((p) => p.is_system_phase && p.system_phase_type !== "prelims");
    const pre = ps.find((p) => p.is_system_phase && p.system_phase_type === "prelims");
    return [...normal, ...(prov ? [prov] : []), ...(pre ? [pre] : [])];
  }, [computed.phases]);

  // Load templates + master phases + library items + labour rates + materials (for modals + drawer) once
  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
    api.get("/library").then((r) => setLibraryItems(r.data || [])).catch(() => {});
    api.get("/labour-rates").then((r) => setLabourRates(r.data || [])).catch(() => {});
    api.get("/materials").then((r) => setMaterialsList(r.data || [])).catch(() => {});
  }, []);

  // LT-Estimate-UX-01 follow-up — job-type-aware phase picker.
  //
  // Refetch the master phase list whenever the tender's job_type
  // changes so the AddPhaseModal only offers phases relevant to this
  // project (e.g. a loft conversion doesn't see Substructure). Master
  // phases with an empty `applies_to_job_types` list apply to
  // everything (backward compat).
  //
  // The tender always keeps its ALREADY-ADDED phases regardless of
  // filter — we only filter the picker, never retroactively hide
  // phases the estimator already put line items into.
  useEffect(() => {
    const qs = tender?.job_type ? `?job_type=${encodeURIComponent(tender.job_type)}` : "";
    api.get(`/settings/master-phases${qs}`).then((r) => setMasterPhases(r.data || [])).catch(() => {});
  }, [tender?.job_type]);

  // LT-Kbd-Nav · Global "?" opens the keyboard shortcuts dialog, unless the
  // user is currently typing in an input / textarea / contenteditable.
  // Accept both `?` (US layouts / most browsers) and Shift+/ (fallback for
  // layouts / test runners where the shifted key doesn't resolve to `?`).
  useEffect(() => {
    const onDocKey = (e) => {
      const isQuestion = e.key === "?" || (e.shiftKey && e.key === "/");
      if (!isQuestion) return;
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      const editable = tag === "input" || tag === "textarea" || tag === "select"
        || document.activeElement?.isContentEditable;
      if (editable) return;
      e.preventDefault();
      setOpenShortcuts(true);
    };
    window.addEventListener("keydown", onDocKey);
    return () => window.removeEventListener("keydown", onDocKey);
  }, []);

  // Debounced save
  const saveTimer = useRef(null);
  const stateRef = useRef({ phases, prelims, oapPct });
  stateRef.current = { phases, prelims, oapPct };
  // Polish §2 — track the reason for the next save so the backend audit
  // log can differentiate "estimate_reordered" from regular "estimate_updated".
  // Stays "edit" by default; `setReorderPending()` flips it to "reorder" before
  // queueSave fires from a drag-and-drop. Reset to "edit" once flushed.
  const reasonRef = useRef("edit");

  const flushSave = useCallback(async () => {
    const { phases: ph, prelims: pr, oapPct: op } = stateRef.current;
    const change_reason = reasonRef.current || "edit";
    reasonRef.current = "edit";
    setStatus("saving");
    try {
      const r = await api.put(`/tenders/${tender.id}/estimate`, {
        phases: ph,
        prelims_total: pr,
        oap_pct: op,
        change_reason,
      });
      setStatus("saved");
      setLastSaved(new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
      // Replace local state with server's authoritative version (refs + computed)
      const nextPhases = r.data.phases || [];
      setPhases(nextPhases);
      onTenderUpdate?.(r.data);
      // Phase 1.5 jump-to-line — if the focused line was edited in this save,
      // clear the lilac wash. If it wasn't (a save on some OTHER line), keep
      // the wash and re-baseline against the server's authoritative state so
      // subsequent drift detection stays accurate.
      if (focusLineId) {
        if (focusEditedRef.current) {
          setFocusLineId(null);
        } else {
          focusBaselineRef.current = JSON.stringify(findFocusedLine(nextPhases));
        }
        focusEditedRef.current = false;
      }
    } catch (e) {
      console.error(e);
      setStatus("error");
      toast.error("Save failed — your changes are safe locally. Will retry.");
    }
  }, [tender.id, onTenderUpdate, focusLineId, findFocusedLine]);

  const queueSave = useCallback((reason) => {
    if (reason) reasonRef.current = reason;
    setStatus("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, 800);
  }, [flushSave]);

  const updatePhases = (next, reason) => {
    setPhases(next);
    // Mark the focused line dirty when its serialised state drifts from the
    // baseline captured at focus-time. Same-line qty/hours/rate edits, adding
    // trades, flag changes — anything that mutates the object — trigger this.
    if (focusLineId && focusBaselineRef.current != null) {
      const now = JSON.stringify(findFocusedLine(next));
      if (now !== focusBaselineRef.current) focusEditedRef.current = true;
    }
    queueSave(reason);
  };
  const updatePrelims = (v) => { setPrelims(v); queueSave(); };
  const updateOap = (v) => { setOapPct(v); queueSave(); };

  // Handlers
  const handlePhaseUpdate = (phaseId) => (updated) => {
    updatePhases(phases.map((p) => p.id === phaseId ? updated : p));
  };
  const handlePhaseRemove = (phaseId) => () => {
    updatePhases(phases.filter((p) => p.id !== phaseId));
  };
  const handlePhaseMove = (phaseId, dir) => () => {
    const idx = phases.findIndex((p) => p.id === phaseId);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= phases.length) return;
    const next = [...phases];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    updatePhases(next);
  };

  const addPhaseDirect = (name) => {
    const p = makeBlankPhase(name);
    updatePhases([...phases, p]);
    setOpenAddPhase(false);
    toast.success(`Phase "${name}" added`);
  };

  // LT-38 — library lines land at qty 0 (amber "enter qty" marker on the
  // cell) so a quantity can never be forgotten and priced as one unit.
  const appendLibraryLines = (libItems) => {
    if (!openLibraryPicker || !libItems.length) return;
    const lines = libItems.map((it) => lineFromLibrary(it, 0));
    const { phase_id, sub_phase_id } = openLibraryPicker;
    const next = phases.map((p) => {
      if (p.id !== phase_id) return p;
      if (sub_phase_id) {
        return {
          ...p,
          sub_phases: p.sub_phases.map((sp) =>
            sp.id !== sub_phase_id ? sp : { ...sp, line_items: [...sp.line_items, ...lines] })
        };
      }
      return { ...p, line_items: [...p.line_items, ...lines] };
    });
    updatePhases(next);
    setOpenLibraryPicker(null);
  };
  const handleLibraryPick = (libItem) => {
    if (!openLibraryPicker) return;
    appendLibraryLines([libItem]);
    toast.success(libItem.has_recipe
      ? `Added from recipe: ${libItem.description.slice(0, 40)}… — enter the qty`
      : `Added: ${libItem.description.slice(0, 40)}… — enter the qty`);
  };
  const handleLibraryPickMany = (libItems) => {
    if (!openLibraryPicker) return;
    appendLibraryLines(libItems);
    toast.success(`Added ${libItems.length} line${libItems.length === 1 ? "" : "s"} from the library — enter the quantities`);
  };

  const applyTemplate = async (tpl) => {
    setOpenTemplate(false);
    setStatus("saving");
    try {
      const r = await api.post(`/tenders/${tender.id}/apply-template`, { template_id: tpl.id });
      setPhases(r.data.phases || []);
      setPrelims(r.data.prelims_total || 0);
      setProvsums(r.data.provsums_total || 0);
      setOapPct(r.data.oap_pct ?? 22);
      onTenderUpdate?.(r.data);
      setStatus("saved");
      setLastSaved(new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
      // LT-21b — report what the template actually did (lines from the live library).
      const rep = templateReportSummary(r.data.template_report);
      const title = `Applied template "${tpl.name}"${rep.text ? ` — ${rep.text}` : ""}`;
      if (rep.warnings.length) toast.warning(title, { description: rep.warnings.join(" · "), duration: 8000 });
      else toast.success(title);
    } catch (e) {
      setStatus("error");
      toast.error(templateErrorMessage(e, "Failed to apply template"));
    }
  };

  const overrideCount = useMemo(() => {
    let n = 0;
    const cnt = (li) => {
      // LT-Estimate-UX-01 Section 3 — include unit_override in the count, and
      // walk each additional_trades row (2 flags per row: hours, hourly_rate).
      ["hours_override", "hourly_rate_override", "material_rate_override",
       "plant_rate_override", "subby_rate_override", "unit_override"].forEach((k) => { if (li[k]) n += 1; });
      for (const t of (li.additional_trades || [])) {
        if (t.hours_override) n += 1;
        if (t.hourly_rate_override) n += 1;
      }
    };
    for (const p of computed.phases) {
      for (const li of p.line_items || []) cnt(li);
      for (const sp of p.sub_phases || []) for (const li of sp.line_items || []) cnt(li);
    }
    return n;
  }, [computed.phases]);

  const expandAll = () => {
    setAllExpanded((x) => !x);
    updatePhases(phases.map((p) => ({ ...p, is_collapsed: allExpanded })));
  };

  const existingNames = useMemo(() => new Set(phases.map((p) => p.name)), [phases]);

  // ============================================================
  // Polish §2 — drag-and-drop reorder
  // ============================================================
  // Sensors: pointer drag with a small activation distance so cell editing
  // clicks aren't accidentally interpreted as drag intents. Keyboard sensor
  // for accessibility.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Active drag metadata { kind, id } drives the DragOverlay preview.
  const [activeDrag, setActiveDrag] = useState(null);

  const handleDragStart = ({ active }) => {
    const meta = parseId(active.id);
    if (meta) setActiveDrag(meta);
  };

  const handleDragCancel = () => setActiveDrag(null);

  const handleDragEnd = ({ active, over }) => {
    setActiveDrag(null);
    if (!over) return;
    if (active.id === over.id) return;

    const aMeta = parseId(active.id);
    if (!aMeta) return;

    // ----- PHASE reorder (top-level list) -----
    if (aMeta.kind === "phase") {
      const oMeta = parseId(over.id);
      if (oMeta?.kind !== "phase") return;
      const fromIdx = phases.findIndex((p) => p.id === aMeta.id);
      const toIdx = phases.findIndex((p) => p.id === oMeta.id);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
      updatePhases(reorderPhases(phases, fromIdx, toIdx), "reorder");
      return;
    }

    // ----- SUB-PHASE move (within or across phases) -----
    if (aMeta.kind === "sub") {
      const target = resolveSubPhaseDropTarget(phases, over.id);
      if (!target) return;
      const found = findSubPhase(phases, aMeta.id);
      if (!found) return;
      // No-op when target is the same slot
      if (
        target.toPhaseId === found.phaseId &&
        (target.toIndex === found.index || target.toIndex == null)
      ) {
        // Allow append-to-end same-phase (target.toIndex == null) only if
        // the sub-phase isn't already the last item.
        const list = phases.find((p) => p.id === found.phaseId)?.sub_phases || [];
        if (target.toIndex == null && found.index === list.length - 1) return;
        if (target.toIndex === found.index) return;
      }
      updatePhases(
        moveSubPhase(phases, aMeta.id, target.toPhaseId, target.toIndex),
        "reorder"
      );
      return;
    }

    // ----- LINE move (within or across containers) -----
    if (aMeta.kind === "line") {
      const target = resolveLineDropTarget(phases, over.id);
      if (!target) return;
      const found = findLine(phases, aMeta.id);
      if (!found) return;
      // No-op same-slot drops
      if (
        target.toPhaseId === found.phaseId &&
        target.toSubPhaseId === found.subPhaseId &&
        (target.toIndex === found.index || target.toIndex == null)
      ) {
        const list = target.toSubPhaseId
          ? (phases.find((p) => p.id === found.phaseId)?.sub_phases || [])
              .find((sp) => sp.id === target.toSubPhaseId)?.line_items || []
          : phases.find((p) => p.id === found.phaseId)?.line_items || [];
        if (target.toIndex == null && found.index === list.length - 1) return;
        if (target.toIndex === found.index) return;
      }
      updatePhases(
        moveLine(phases, aMeta.id, target.toPhaseId, target.toSubPhaseId, target.toIndex),
        "reorder"
      );
      return;
    }
  };

  // DragOverlay preview — a lightweight, table-free representation so
  // browsers don't render an orphan <tr>/<div> in the wrong context.
  const renderDragOverlay = () => {
    if (!activeDrag) return null;
    if (activeDrag.kind === "phase") {
      const p = phases.find((x) => x.id === activeDrag.id);
      if (!p) return null;
      return (
        <div className="bg-surface border border-accent rounded-lg shadow-card px-4 py-2.5 inline-flex items-center gap-2.5"
          style={{ minWidth: 280 }}>
          <GripVertical size={14} className="text-accent" />
          <span className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
            {p.name}
          </span>
          <span className="text-ink-3" style={{ fontSize: 11.5 }}>
            · {(p.line_items?.length || 0) +
                (p.sub_phases || []).reduce((s, sp) => s + (sp.line_items?.length || 0), 0)} items
          </span>
        </div>
      );
    }
    if (activeDrag.kind === "sub") {
      const found = findSubPhase(phases, activeDrag.id);
      if (!found) return null;
      const sp = phases.find((p) => p.id === found.phaseId)?.sub_phases?.[found.index];
      if (!sp) return null;
      return (
        <div className="bg-surface border border-accent rounded shadow-card px-3 py-1.5 inline-flex items-center gap-2"
          style={{ background: "#F5F3EE", minWidth: 240 }}>
          <GripVertical size={11} className="text-accent" />
          <span className="col-head text-ink-2" style={{ fontWeight: 700 }}>SUB-PHASE</span>
          <span className="col-head text-ink" style={{ fontWeight: 700 }}>{sp.name}</span>
        </div>
      );
    }
    if (activeDrag.kind === "line") {
      const found = findLine(phases, activeDrag.id);
      if (!found) return null;
      return (
        <div className="bg-surface border border-accent rounded shadow-card px-3 py-2 inline-flex items-center gap-2"
          style={{ maxWidth: 480 }}>
          <GripVertical size={12} className="text-accent" />
          <span className="font-mono tnum text-ink-3" style={{ fontSize: 11 }}>
            {found.line.ref || "—"}
          </span>
          <span className="text-ink truncate" style={{ fontSize: 12 }}>
            {found.line.description || "(no description)"}
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <FocusLineContext.Provider value={focusCtx}>
    <InternalNoteContext.Provider value={internalCtx}>
    <ArchivedRefsContext.Provider value={archivedRefs}>
    <div className="mt-6">
      {/* LT-17 §7d — passive labour-rate staleness banner. Reads
          `/tenders/{id}/labour-rate-staleness` and only renders when
          any master rate used by this tender has moved since the
          tender was created. Informational; ignoring it costs
          nothing. The Revise flow is where a live tender's rates
          actually get changed (LT-17 §7e). */}
      <LabourRateStalenessBanner tender={tender} />

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {overrideCount > 0 && (
            <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md"
              style={{ background: "#FEF3E2", color: "#7A4C0A", fontSize: 11.5, fontWeight: 500, border: "1px solid #F2DFB8" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#D97706" }} />
              <span className="font-mono tnum">{overrideCount}</span> cells overridden
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <QuietButton icon={Keyboard} onClick={() => setOpenShortcuts(true)} data-testid="kbd-shortcuts-open">
            Keyboard
          </QuietButton>
          <QuietButton icon={allExpanded ? Minimize2 : Maximize2} onClick={expandAll}>
            {allExpanded ? "Collapse all" : "Expand all"}
          </QuietButton>
          <QuietButton icon={Filter}>Filter</QuietButton>
          {phases.length > 0 && (
            <>
              <QuietButton icon={RefreshCw} onClick={() => setOpenBulkRefresh(true)} data-testid="estimate-refresh-all">Refresh all</QuietButton>
              <QuietButton icon={FileText} onClick={() => setOpenTemplate(true)}>Apply template</QuietButton>
              <PrimaryButton icon={Plus} onClick={() => setOpenAddPhase(true)}>Add phase</PrimaryButton>
            </>
          )}
        </div>
      </div>

      {/* LT-20 follow-up — what wastage adds to materials, per category
          (estimate maths; refreshes after each autosave) */}
      {phases.length > 0 && (
        <div className="mb-4 space-y-3">
          <WastageInsightsPanel
            tenderId={tender.id}
            refreshKey={status === "saved" ? `${lastSaved}-${refreshTick}` : null}
            storageKey="lt20_wastage_open_estimate"
          />
          {/* LT-36 — materials money still on flat rates (whole tender) */}
          <FlatMaterialsPanel
            tenderId={tender.id}
            refreshKey={status === "saved" ? `${lastSaved}-${refreshTick}` : null}
            storageKey="lt36_flat_open_estimate"
          />
        </div>
      )}

      {/* Body */}
      {computed.phases.length === 0 ? (
        <EmptyEstimate
          onApplyTemplate={() => setOpenTemplate(true)}
          onAddPhase={() => setOpenAddPhase(true)}
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={orderedPhases.map((p) => ID.phase(p.id))}
            strategy={verticalListSortingStrategy}
          >
            {orderedPhases.map((p) => (
              p.is_system_phase ? (
                <SystemPhaseCard key={p.id} tenderId={tender.id} tender={tender} phase={p} />
              ) : (
              <PhaseCard key={p.id}
                phase={p}
                onPhaseUpdate={handlePhaseUpdate(p.id)}
                onRemove={handlePhaseRemove(p.id)}
                onMoveUp={handlePhaseMove(p.id, -1)}
                onMoveDown={handlePhaseMove(p.id, 1)}
                onOpenLibraryPicker={(phase_id, sub_phase_id) =>
                  setOpenLibraryPicker({ phase_id, sub_phase_id })}
                onOpenRecipeDrawer={(line) => {
                  // Locate the line's phase + sub-phase so the drawer save bubbles
                  // back to the correct slot. We re-locate at save time (not here)
                  // so a stale closure doesn't break if the line is moved between
                  // sub-phases via the row menu while the drawer is open.
                  setRecipeDrawer({ line, phaseId: p.id });
                }}
                onSaveToLibrary={(line) => setSaveToLibLine(line)}
                onUpdateLibraryFromLine={(line) => setUpdateLibFromLine(line)}
                onRefreshFromLibrary={(line) => setRefreshLine(line)}
                activeSubPhase={activeSubPhase}
                onActivateSubPhase={setActiveSubPhase}
                libraryDrift={libraryDrift}
                labourRates={labourRates}
              />
              )
            ))}
          </SortableContext>

          <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
            {renderDragOverlay()}
          </DragOverlay>

          <div className="flex items-center gap-2 mt-2">
            <QuietButton icon={Plus} onClick={() => setOpenAddPhase(true)}>Add phase</QuietButton>
            <QuietButton icon={FileText} onClick={() => setOpenTemplate(true)}>Apply another template</QuietButton>
          </div>
        </DndContext>
      )}

      <ApplyTemplateModal
        open={openTemplate} onClose={() => setOpenTemplate(false)}
        templates={templates} onApply={applyTemplate}
      />
      <AddPhaseModal
        open={openAddPhase} onClose={() => setOpenAddPhase(false)}
        masterPhases={masterPhases} existingNames={existingNames}
        onAdd={addPhaseDirect}
      />
      <LibraryPickerModal
        open={!!openLibraryPicker}
        onClose={() => setOpenLibraryPicker(null)}
        items={libraryItems}
        currentPhaseTags={
          openLibraryPicker
            ? [phases.find((p) => p.id === openLibraryPicker.phase_id)?.name].filter(Boolean)
            : []
        }
        onPick={handleLibraryPick}
        onPickMany={handleLibraryPickMany}
        onBackfill={async () => {
          // Section 5 — backend hygiene pass: fills missing SKUs and
          // refreshes has_recipe across all items. Replaces local cache
          // with the freshly-loaded list so the modal reflects normalised
          // data immediately.
          try {
            const r = await api.post("/library/backfill");
            if (r.data?.items) setLibraryItems(r.data.items);
          } catch (e) {
            console.error("library backfill failed", e);
          }
        }}
      />

      {/* Phase 9 — per-line recipe drawer (Section 1) */}
      <RecipeDrawer
        open={!!recipeDrawer}
        line={recipeDrawer?.line || null}
        tenderId={tender.id}
        labourRates={labourRates}
        materials={materialsList}
        masterPhases={(masterPhases || []).map((m) => m.name).filter(Boolean)}
        onClose={() => setRecipeDrawer(null)}
        onSave={(updatedLine) => {
          // Walk the phases tree and replace the line by id wherever it lives
          // (root of phase or inside a sub-phase). This is resilient to the line
          // being moved between sub-phases while the drawer was open.
          const targetPhaseId = recipeDrawer?.phaseId;
          setPhases((prev) => prev.map((p) => {
            if (p.id !== targetPhaseId) return p;
            const replaceInList = (list) =>
              list.map((l) => l.id === updatedLine.id ? updatedLine : l);
            return {
              ...p,
              line_items: replaceInList(p.line_items || []),
              sub_phases: (p.sub_phases || []).map((sp) => ({
                ...sp,
                line_items: replaceInList(sp.line_items || [])
              }))
            };
          }));
        }}
        onApplyToLine={({ line, arrays }) => {
          // Same as above — applies the modal's recipe arrays to the source line
          // when the user opts into "Also apply this recipe to the line" inside
          // the SaveToLibraryModal that the drawer renders.
          if (!line) return;
          const updatedLine = applyRecipeArraysToLine(line, arrays);
          setPhases((prev) => prev.map((p) => {
            const replaceInList = (list) =>
              (list || []).map((l) => l.id === updatedLine.id ? updatedLine : l);
            return {
              ...p,
              line_items: replaceInList(p.line_items),
              sub_phases: (p.sub_phases || []).map((sp) => ({
                ...sp,
                line_items: replaceInList(sp.line_items)
              }))
            };
          }));
        }}
      />

      {/* Section 4 — Save line as new library item (triggered from row-menu) */}
      <SaveToLibraryModal
        open={!!saveToLibLine}
        sourceLine={saveToLibLine}
        tenderId={tender.id}
        masterPhases={(masterPhases || []).map((m) => m.name).filter(Boolean)}
        labourRates={labourRates}
        materials={materialsList}
        onClose={() => setSaveToLibLine(null)}
        onCreated={(lib) => {
          // Refresh the library cache so any subsequent "From library" picker
          // opens see the newly-created item without a manual reload.
          setLibraryItems((prev) => [lib, ...prev]);
          setSaveToLibLine(null);
        }}
        onApplyToLine={({ line, arrays }) => {
          // Option (b) — apply the freshly-saved recipe back onto the source
          // tender line so the user doesn't have to also click "Save recipe to
          // this line" from the drawer. Walks every phase + sub-phase and
          // replaces the line by id (resilient to mid-edit moves).
          if (!line) return;
          const updatedLine = applyRecipeArraysToLine(line, arrays);
          setPhases((prev) => prev.map((p) => {
            const replaceInList = (list) =>
              (list || []).map((l) => l.id === updatedLine.id ? updatedLine : l);
            return {
              ...p,
              line_items: replaceInList(p.line_items),
              sub_phases: (p.sub_phases || []).map((sp) => ({
                ...sp,
                line_items: replaceInList(sp.line_items)
              }))
            };
          }));
        }}
      />

      {/* LT-Estimate-UX-01 Section 3 — Update library item with row's edited values */}
      <UpdateLibraryFromLineDialog
        open={!!updateLibFromLine}
        sourceLine={updateLibFromLine}
        tenderId={tender.id}
        onClose={() => setUpdateLibFromLine(null)}
        onSuccess={(updatedLibItem) => {
          // Refresh the library cache so future picker opens see the new values.
          if (updatedLibItem && updatedLibItem.id) {
            setLibraryItems((prev) => prev.map((li) => li.id === updatedLibItem.id ? updatedLibItem : li));
          }
        }}
      />

      {/* LT-20 — Refresh quantities from library (old → new, confirm first) */}
      <RefreshFromLibraryDialog
        open={!!refreshLine}
        tenderId={tender.id}
        line={refreshLine}
        onClose={() => setRefreshLine(null)}
        onApplied={(t) => {
          // Server is authoritative after a refresh: adopt its phases so the
          // re-snapshotted entries + recomputed rate replace local state.
          if (t && t.phases) setPhases(t.phases);
          setRefreshTick((n) => n + 1);
          onTenderUpdate?.(t);
        }}
      />

      {/* LT-20 follow-up — whole-tender refresh, one confirm */}
      <BulkRefreshDialog
        open={openBulkRefresh}
        tenderId={tender.id}
        onClose={() => setOpenBulkRefresh(false)}
        onApplied={(t) => {
          if (t && t.phases) setPhases(t.phases);
          setRefreshTick((n) => n + 1);
          onTenderUpdate?.(t);
        }}
      />

      {/* Sticky totals strip */}
      <div style={{ height: 12 }} />
      <TotalsStrip
        totals={computed}
        prelims={prelims} provsums={provsums} oapPct={oapPct}
        onPrelimsChange={updatePrelims}
        onOapPctChange={updateOap}
        status={status} lastSaved={lastSaved}
      />

      {/* LT-Kbd-Nav · shortcuts panel — opened by toolbar button or "?" hotkey */}
      <KeyboardShortcutsDialog open={openShortcuts} onOpenChange={setOpenShortcuts} />

      {/* Item 3 — Internal note popover (shared by line & phase glyphs) */}
      {internalPopover ? (
        <InternalNotePopover
          tenderId={tender.id}
          scope={internalPopover.scope}
          targetId={internalPopover.targetId}
          targetLabel={internalPopover.targetLabel}
          open={true}
          onClose={() => setInternalPopover(null)}
          onChange={reloadInternalCounts}
        />
      ) : null}
    </div>
    </ArchivedRefsContext.Provider>
    </InternalNoteContext.Provider>
    </FocusLineContext.Provider>
  );
}
